import { observer } from 'mobx-react'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { EXPLORER_URLS, RADOM_COLORS } from '../util/Constants'
import { formatExplorerUrl } from '../util/Chains'
import Table from '../components/Table'
import Radom, {
  BalanceAdjustmentTransaction,
  Deposit,
  ICheckoutSession,
  DepositBeneficiaryDetails,
  ManagedBalanceLedger,
  ManagedEvent,
  ManagedEventDepositData,
  ManagedEventPaymentData,
  ManagedEventTransaction,
  ManagedEventWithdrawalData,
  ManagedEventWithRelatedCustomer,
  PayerFinancialAddress,
  Payout,
  RefundEntityWithRelatedCustomer,
  RefundStatus,
  Swap,
  SwapStatus,
  Transaction,
  TransactionKind,
  TransactionStatus,
  WithdrawalAccount,
  WithdrawalRequest,
  WithdrawalRequestWithAccountName,
  WithdrawalStatus
} from '../state/Radom'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import { PrimaryButton, SecondaryButton } from '../components/Button'
import { testNetworks, prodNetworks, getMethod } from '../util/Managed'
import { payoutStatusLabel } from '../util/PayoutStatus'
import { ledgerScope, ledgerTicker } from '../util/Ledger'
import { cutString, errorToast, formatAssetAmount, roundAssetAmount, ROUTES, shortAddress, unifyLedger, useTestMode } from '../util/Util'
import Pagination from '../components/Pagination'
import PayoutTransactionLogo from '../images/PayoutTransaction.svg'
import WithdrawalTransactionLogo from '../images/WithdrawalTransaction.svg'
import PaymentTransactionLogo from '../images/PaymentTransaction.svg'
import DepositTransactionLogo from '../images/DepositTransaction.svg'
import RefundTransactionLogo from '../images/RefundTransaction.svg'
import SwapTransactionLogo from '../images/SwapTransaction.svg'
import TimeText, { TimeFormatType } from '../components/TimeText'
import styled from 'styled-components'
import { AssociatedEntityType, ManagedPaymentEvent, parseManagedEvent } from '../state/Events'
import { Chevron } from '../icons/Chevron'
import ExportTransactionsForm from '../components/ExportTransactionsForm'
import Modal from '../components/Modal'
import Dots from '../icons/Dots'
import PageHeader from '../components/PageHeader'
import _ from 'lodash'
import { TextInput } from '../components/Input'
import CopyButton from '../components/CopyButton'
import PayoutRecipients from '../components/PayoutRecipients'
import RefundForm from '../components/RefundForm'
import {
  ListPage,
  ListPaginationRow,
  ListTableSurface,
  PageActionRow,
  PageHeaderRow
} from '../components/DashboardPrimitives'
import DetailsDrawer, {
  DrawerCard,
  DrawerDetailLabel,
  DrawerDetailRow,
  DrawerDetailValue
} from '../components/DetailsDrawer'
import LinkExternal from '../components/LinkExternal'
import {
  DetectedRow,
  DetectedTransactionDrawer,
  detectedProgressLabel,
  detectedStatusLabel,
  useDetectedTransactions
} from '../components/DetectedTransactions'
import { usePermissions } from '../util/permissions'
import User from '../state/User'
import OpenBankingPaymentController from '../state/OpenBankingPaymentController'
import { openBankingStatusFromProvider, openBankingStatusPresentation } from '../util/OpenBankingPayment'

export interface TransactionSummary {
  fee: {
    amount: string | number
    ticker: string
  }
  in: {
    amount: string | number
    ticker: string
    color: string
  }
  out?: {
    amount: string | number
    ticker: string
    color: string
  }
}

export interface ExtendedTransaction extends Transaction {
  status: TransactionStatus
  summary: TransactionSummary
  related: { url: string, name: string }
  url: string
}

interface DrawerDetailConfig {
  label: string
  value: React.ReactNode
}

interface TransactionDrawerSupplements {
  depositEventData?: ManagedEventDepositData
  withdrawalEventData?: ManagedEventWithdrawalData
  withdrawalAccount?: WithdrawalAccount
}

// Arrival expectations shown beside processing stages — ranges, not promises.
export const STAGE_EXPECTATIONS: Record<string, string> = {
  'Preparing funds': 'usually within a few hours',
  'Converting funds': 'usually a few minutes',
  'Transferring funds': 'usually within 1 business day',
  'Paying recipient': 'usually within minutes'
}

export const TransactionStatusBadge = styled.div<{ status: TransactionStatus }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  width: fit-content;

  ${({ status }) => {
    let bgColor = RADOM_COLORS.ERROR_LIGHT as string
    let textColor = RADOM_COLORS.ERROR as string

    switch (status) {
    case TransactionStatus.Pending:
    case TransactionStatus.Processing:
      bgColor = RADOM_COLORS.GRAY_MED as string
      textColor = RADOM_COLORS.GRAY_DARKEST as string
      break
    case TransactionStatus.Successful:
      bgColor = RADOM_COLORS.SUCCESS_LIGHT as string
      textColor = RADOM_COLORS.SUCCESS as string
      break
    case TransactionStatus.Incomplete:
    case TransactionStatus.InReview:
      bgColor = RADOM_COLORS.WARNING_LIGHT as string
      textColor = RADOM_COLORS.WARNING as string
      break
    }

    return `
    background-color: ${bgColor};
    color: ${textColor};
  `
  }};
`

const AmountCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`

const AmountLine = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  font-weight: 500;
  opacity: 0.9;
`

const AmountTicker = styled.span`
  font-weight: 500;
  opacity: 0.7;
`

const ConversionLine = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 12px;
  font-weight: 400;
`

const FeeLine = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  font-weight: 500;
`

const BalanceEntry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const BalanceMeta = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${RADOM_COLORS.GRAY4};
`

const FiltersPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid var(--surface-pressed, #edf1f7);
  background: var(--surface, #ffffff);
`

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`

const FilterSearchWrap = styled.div`
  flex: 1 1 360px;
  min-width: 280px;
`

const FilterControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-left: auto;
`

const FilterTrigger = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const FilterTriggerLabel = styled.span`
  color: #8a93a3;
  font-size: 10.5px;
  font-weight: 500;
  line-height: 1.1;
  text-transform: uppercase;
`

const FilterTriggerValue = styled.span`
  color: var(--ink, #20242d);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FilterCount = styled.span`
  background: var(--surface-muted, #f8fafc);
  border: 1px solid var(--border, #e6ebf3);
  border-radius: 999px;
  color: var(--text-faint, #7b8392);
  font-size: 11.5px;
  font-weight: 500;
  padding: 3px 7px;
`

const FilterResetButton = styled.button`
  background: transparent;
  border: 0;
  color: var(--text-muted, #4f5968);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 500;
  padding: 6px 0;

  &:hover {
    color: var(--ink-strong, #111111);
  }

  &:disabled {
    color: var(--text-disabled, #a4abb8);
    cursor: default;
  }
`

const FilterMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  width: 300px;
`

const FilterMenuSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`

const FilterMenuTitle = styled.span`
  color: var(--text-soft, #818a9b);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
`

const FilterPresetGrid = styled.div`
  display: grid;
  gap: 6px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`

const FilterMenuButton = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => $active ? 'var(--surface-pressed, #eef1f7)' : 'var(--surface, #ffffff)'};
  border: 1px solid ${({ $active }) => $active ? 'var(--ink, #20242d)' : 'var(--border, #e2e7ef)'};
  border-radius: 9px;
  color: ${({ $active }) => $active ? 'var(--ink, #20242d)' : 'var(--text-soft, #7d8492)'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12.25px;
  font-weight: ${({ $active }) => $active ? 500 : 400};
  min-height: 34px;
  padding: 7px 9px;
  text-align: left;
  transition: 0.16s ease border-color, 0.16s ease background-color, 0.16s ease color;

  &:hover {
    background: var(--surface-muted, #f8fafc);
    border-color: var(--border-strong, #cbd3df);
    color: var(--ink, #20242d);
  }
`

const FilterOptionGrid = styled.div`
  display: grid;
  gap: 4px;
`

const FilterOptionRow = styled.button<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) => $active ? 'var(--surface-muted, #f8fafc)' : 'var(--surface, #ffffff)'};
  border: 0;
  border-radius: 8px;
  color: var(--ink, #20242d);
  cursor: pointer;
  display: flex;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: ${({ $active }) => $active ? 500 : 400};
  gap: 9px;
  justify-content: flex-start;
  min-height: 32px;
  padding: 7px 8px;
  text-align: left;
  transition: 0.16s ease background-color;

  &:hover {
    background: var(--surface-hover, #f5f7fb);
  }
`

const FilterCheck = styled.span<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) => $active ? 'var(--ink, #20242d)' : 'var(--surface, #ffffff)'};
  border: 1px solid ${({ $active }) => $active ? 'var(--ink, #20242d)' : 'var(--border-strong, #cbd3df)'};
  border-radius: 4px;
  display: inline-flex;
  flex: 0 0 14px;
  height: 14px;
  justify-content: center;
  width: 14px;

  &::after {
    border: solid var(--surface, #ffffff);
    border-width: 0 1.5px 1.5px 0;
    content: '';
    display: block;
    height: 7px;
    opacity: ${({ $active }) => $active ? 1 : 0};
    transform: rotate(45deg) translate(-1px, -1px);
    width: 4px;
  }
`

const TransactionCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const TransactionMetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-faint, #7b8392);
  font-size: 12px;
  min-width: 0;
`

const RelatedCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const DrawerHeroCard = styled(DrawerCard)`
  gap: 18px;
  padding: 20px;
`

const DrawerHeroTop = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-width: 0;
`

const DrawerHeroIdentity = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 0;
`

const DrawerHeroIcon = styled.div`
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  height: 42px;
  justify-content: center;
  width: 42px;

  img {
    height: 36px;
    width: 36px;
  }
`

const DrawerHeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

const DrawerHeroTitle = styled.span`
  color: var(--ink, #20242d);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.25;
`

const DrawerHeroMeta = styled.span`
  color: var(--text-faint, #7b8494);
  font-size: 12.5px;
  line-height: 1.35;
`

const DrawerSectionTitle = styled.span`
  color: var(--text-faint, #7b8494);
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.02em;
  line-height: 1.35;
  margin-bottom: 12px;
  text-transform: uppercase;
`

const DrawerHero = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const DrawerHeroAmount = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const DrawerHeroAmountValue = styled.span`
  font-size: 32px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.05;
`

const DrawerHeroTicker = styled.span`
  color: #737b8b;
  font-size: 16px;
  font-weight: 500;
`

const DrawerSubline = styled.span`
  color: #737b8b;
  font-size: 12.5px;
  line-height: 1.4;
`

const DrawerMetricGrid = styled.div`
  border-top: 1px solid var(--surface-pressed, #edf1f7);
  display: grid;
  gap: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding-top: 14px;
`

const DrawerMetric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  &:not(:first-child) {
    border-left: 1px solid var(--surface-pressed, #edf1f7);
    padding-left: 16px;
  }
`

const DrawerMetricLabel = styled.span`
  color: var(--text-faint, #7b8494);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.35;
  text-transform: uppercase;
`

const DrawerMetricValue = styled.span`
  color: var(--ink, #20242d);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
  overflow-wrap: anywhere;
`

const DrawerReferenceInline = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 6px;
  justify-content: flex-end;
  max-width: 100%;
  min-width: 0;
`

const DrawerMono = styled.span`
  color: #4b5563;
  font-family: var(--font-mono);
  overflow-wrap: anywhere;
`

const DrawerValueStack = styled.span`
  align-items: flex-end;
  display: inline-flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

const DrawerSecondaryValue = styled.span`
  color: var(--text-faint, #7b8494);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.35;
  overflow-wrap: anywhere;
`

const DrawerEmptyNote = styled.span`
  color: var(--text-faint, #7b8494);
  font-size: 12.5px;
  line-height: 1.45;
`

const TRANSACTION_ICONS = {
  [TransactionKind.Deposit]: DepositTransactionLogo,
  [TransactionKind.Withdrawal]: WithdrawalTransactionLogo,
  [TransactionKind.Payment]: PaymentTransactionLogo,
  [TransactionKind.Payout]: PayoutTransactionLogo,
  [TransactionKind.Refund]: RefundTransactionLogo,
  [TransactionKind.Swap]: SwapTransactionLogo,
  [TransactionKind.Credit]: DepositTransactionLogo,
  [TransactionKind.Debit]: WithdrawalTransactionLogo
}

const ADJUSTMENT_TITLES: Record<string, string> = {
  [TransactionKind.Credit]: 'Balance credit',
  [TransactionKind.Debit]: 'Balance debit'
}

const isAdjustmentKind = (kind: TransactionKind): boolean =>
  kind === TransactionKind.Credit || kind === TransactionKind.Debit

const MONEY_OUT_COLOR = '#9a5b00'
const MONEY_IN_COLOR = RADOM_COLORS.NICE_GREEN
const CONVERSION_COLOR = RADOM_COLORS.RADOM_BLUE

const FIAT_RAIL_LABELS: Record<string, string> = {
  ach: 'ACH',
  ach_push: 'ACH',
  ach_pull: 'ACH',
  wire: 'Wire',
  fedwire: 'Wire',
  sepa: 'SEPA',
  swift: 'SWIFT',
  fps: 'FPS',
  bacs: 'BACS',
  chaps: 'CHAPS',
  spei: 'SPEI',
  pix: 'PIX',
  eft: 'EFT',
  rtp: 'RTP'
}

const NON_FIAT_RAIL_VALUES = new Set([
  'wallet',
  'crypto',
  'blockchain',
  'ethereum',
  'polygon',
  'base',
  'bitcoin',
  'solana',
  'tron'
])

const TRANSACTION_KIND_PRESETS: Array<{ label: string, kinds: TransactionKind[] }> = [
  {
    label: 'All movement',
    kinds: Object.values(TransactionKind)
  },
  {
    label: 'Money in',
    kinds: [TransactionKind.Deposit, TransactionKind.Payment, TransactionKind.Credit]
  },
  {
    label: 'Money out',
    kinds: [TransactionKind.Withdrawal, TransactionKind.Payout, TransactionKind.Refund, TransactionKind.Debit]
  },
  {
    label: 'Funding',
    kinds: [TransactionKind.Deposit, TransactionKind.Withdrawal]
  },
  {
    label: 'Payments',
    kinds: [TransactionKind.Payment, TransactionKind.Refund]
  },
  {
    label: 'Conversions',
    kinds: [TransactionKind.Swap]
  }
]

const TRANSACTION_STATUS_PRESETS: Array<{ label: string, statuses: TransactionStatus[] }> = [
  {
    label: 'All statuses',
    statuses: Object.values(TransactionStatus)
  },
  {
    label: 'Settled',
    statuses: [TransactionStatus.Successful]
  },
  {
    label: 'Needs attention',
    statuses: [TransactionStatus.Pending, TransactionStatus.InReview, TransactionStatus.Incomplete, TransactionStatus.Failed]
  },
  {
    label: 'Pending',
    statuses: [TransactionStatus.Pending, TransactionStatus.Processing]
  },
  {
    label: 'Failed',
    statuses: [TransactionStatus.Failed]
  }
]

const Transactions = observer((): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [testMode] = useTestMode()
  const selectedTransactionParam = searchParams.get('selected')?.trim()
  const detectedParam = searchParams.get('detected')
  const transactionQueryParam = searchParams.get('q')?.trim()
  const pageSize = 20
  const [curPage, setCurPage] = useState(0)

  const [isLoading, setIsLoading] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [extendedTransactions, setExtendedTransactions] = useState<ExtendedTransaction[]>([])
  const [exportTransactionsModalOpen, setExportTransactionsModalOpen] = useState(false)
  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger[]>([])
  const [transactionSearch, setTransactionSearch] = useState('')
  const [selectedTransactionId, setSelectedTransactionId] = useState<string>()
  const [selectedDetectedId, setSelectedDetectedId] = useState<string>()
  const { rows: detectedRows } = useDetectedTransactions(testMode)
  const appliedDetectedParam = useRef<string>()
  const [selectedDepositEventData, setSelectedDepositEventData] = useState<ManagedEventDepositData>()
  const [selectedWithdrawalEventData, setSelectedWithdrawalEventData] = useState<ManagedEventWithdrawalData>()
  const [selectedWithdrawalAccount, setSelectedWithdrawalAccount] = useState<WithdrawalAccount>()
  const [isDrawerSupplementLoading, setIsDrawerSupplementLoading] = useState(false)
  const [hasAppliedInitialQueryParams, setHasAppliedInitialQueryParams] = useState(false)
  const [isIncompleteModalOpen, setIsIncompleteModalOpen] = useState(false)
  const [refundSession, setRefundSession] = useState<ICheckoutSession>()
  const [isLoadingRefundSession, setIsLoadingRefundSession] = useState(false)
  const [isCompletingPayment, setIsCompletingPayment] = useState(false)
  const unifiedTreasury = User.organization?.features?.isUnifiedTreasuryEnabled === true

  const [totalTransactions, setTotalTransactions] = useState<number>(0)
  // Each fetch takes a ticket; a response whose ticket is stale is dropped so
  // a slow older search cannot overwrite the newer one.
  const fetchTicket = useRef(0)

  const [transactionKinds, setTransactionKinds] = useState<TransactionKind[]>([
    TransactionKind.Deposit,
    TransactionKind.Payment,
    TransactionKind.Payout,
    TransactionKind.Withdrawal,
    TransactionKind.Refund,
    TransactionKind.Swap,
    TransactionKind.Credit,
    TransactionKind.Debit
  ])
  const [transactionStatuses, setTransactionStatuses] = useState<TransactionStatus[]>([
    TransactionStatus.Successful, TransactionStatus.Pending, TransactionStatus.Processing,
    TransactionStatus.InReview, TransactionStatus.Incomplete, TransactionStatus.Failed
  ])
  const { has } = usePermissions()
  const fetchTransactions = async ({
    page,
    kinds,
    statuses,
    isTestMode,
    search
  }: {
    page: number
    kinds: TransactionKind[]
    statuses: TransactionStatus[]
    isTestMode: boolean
    search: string
  }): Promise<void> => {
    const ticket = ++fetchTicket.current
    setIsLoading(true)
    setTransactions([])
    let transactions: Transaction[]
    try {
      const data = await Radom.listTransactions(
        page * pageSize,
        pageSize,
        kinds,
        statuses,
        isTestMode ? testNetworks : prodNetworks,
        new Date(0).toISOString(),
        new Date().toISOString(),
        isTestMode,
        search.trim() || undefined
      )
      if (ticket !== fetchTicket.current) return
      transactions = data.data
      setTransactions(data.data)
      setTotalTransactions(data.total)
    } catch {
      if (ticket !== fetchTicket.current) return
      setIsLoading(false)
      errorToast('Failed to load transactions')
      return
    }

    try {
      if (transactions.length !== 0) {
        const ids = transactions.flatMap(item => {
          if (item.kind === TransactionKind.Payment) {
            const managedPaymentEvent =
              parseManagedEvent(item.payload as ManagedEventWithRelatedCustomer) as ManagedPaymentEvent

            return [managedPaymentEvent.associatedEntity.id]
          }
          if (item.kind === TransactionKind.Payout) {
            const payout = item.payload as Payout
            return [item.id, ...(payout.fundingWithdrawalIds ?? []), ...(payout.fundingConversionIds ?? [])]
          }
          return [item.id]
        })
        const ledger = await Radom.listManagedBalanceLedger(ids)
        if (ticket !== fetchTicket.current) return
        setManagedBalanceLedger(ledger)
      } else {
        setManagedBalanceLedger([])
      }
    } catch (e) {
      if (ticket !== fetchTicket.current) return
      errorToast('Failed to load ledger')
    }

    setIsLoading(false)
  }

  const debouncedFetch = useMemo(() => _.debounce(fetchTransactions, 300), [])

  const transactionStatusLabel = (status: TransactionStatus): string =>
    status === TransactionStatus.InReview ? 'In review' : status

  const getTransactionStatus = (transaction: Transaction): TransactionStatus => {
    const kind = transaction.kind
    switch (kind) {
    case TransactionKind.Withdrawal: {
      const withdrawal = transaction.payload as WithdrawalRequest
      if (withdrawal.withdrawalStatus === WithdrawalStatus.Complete) {
        return TransactionStatus.Successful
      }
      if (withdrawal.withdrawalStatus === WithdrawalStatus.Failed) {
        return TransactionStatus.Failed
      }
      if (withdrawal.withdrawalStatus === WithdrawalStatus.InReview) {
        return TransactionStatus.InReview
      }
      // Everything else (pending, processing, pendingApproval, and the
      // unified-treasury awaitingInventory wait state) reads as pending.
      return TransactionStatus.Pending
    }
    case TransactionKind.Payment: {
      const payment = transaction.payload as ManagedEvent
      if (payment.eventType === 'incompletePayment') {
        return TransactionStatus.Incomplete
      }
      return TransactionStatus.Successful
    }
    case TransactionKind.Deposit: {
      const deposit = transaction.payload as Deposit
      if (deposit.status === 'failed') {
        return TransactionStatus.Failed
      }
      if (deposit.status === 'inReview') {
        return TransactionStatus.InReview
      }
      return TransactionStatus.Successful
    }
    case TransactionKind.Payout: {
      const payout = transaction.payload as Payout
      if (payout.status === 'Complete') {
        return TransactionStatus.Successful
      }
      if (payout.status === 'Failed' || payout.status === 'Expired') {
        return TransactionStatus.Failed
      }
      if (payout.status === 'InReview') {
        return TransactionStatus.InReview
      }
      if (payout.status === 'Processing') {
        return TransactionStatus.Processing
      }
      return TransactionStatus.Pending
    }
    case TransactionKind.Refund:{
      const refund = transaction.payload as RefundEntityWithRelatedCustomer
      if (refund.status === RefundStatus.Success) return TransactionStatus.Successful
      if (refund.status === RefundStatus.Pending) return TransactionStatus.Pending
      if (refund.status === RefundStatus.InReview) return TransactionStatus.InReview
      return TransactionStatus.Failed
    }
    case TransactionKind.Swap: {
      const swap = transaction.payload as Swap
      if (swap.fundsAvailable && swap.status !== SwapStatus.Failed) return TransactionStatus.Successful
      if (swap.status === SwapStatus.Finalized) return TransactionStatus.Successful
      if (swap.status === SwapStatus.Failed) return TransactionStatus.Failed
      return TransactionStatus.Pending
    }
    case TransactionKind.Credit:
    case TransactionKind.Debit:
      // An adjustment is only listed once it is booked.
      return TransactionStatus.Successful
    }
    return TransactionStatus.Successful
  }

  const getUrl = (transaction: Transaction): string => {
    const kind = transaction.kind
    switch (kind) {
    case TransactionKind.Withdrawal: {
      return `/withdrawal/${transaction.id}?source=transactions`
    }
    case TransactionKind.Payment: {
      return `/payment/${transaction.id}?source=transactions`
    }
    case TransactionKind.Payout: {
      const payout = transaction.payload as Payout
      const entryCount = (payout.cryptoEntries?.length ?? 0) + (payout.fiatEntries?.length ?? 0) || payout.entryCount
      const route = entryCount > 1
        ? ROUTES.getMassivePayoutDetailRoute(transaction.id)
        : ROUTES.getPayoutDetailRoute(transaction.id)
      return `${route}?source=transactions`
    }
    case TransactionKind.Refund: {
      return `/refund/${transaction.id}?source=transactions`
    }
    case TransactionKind.Swap: {
      return `/swap/${transaction.id}?source=transactions`
    }
    case TransactionKind.Credit:
    case TransactionKind.Debit: {
      return `/transactions?selected=${transaction.id}&q=${transaction.id}`
    }
    }
    return `/deposit/${transaction.id}?source=transactions`
  }

  const getTransactionSummary = (transaction: Transaction): TransactionSummary => {
    const kind = transaction.kind
    switch (kind) {
    case TransactionKind.Withdrawal: {
      const withdrawal = transaction.payload as WithdrawalRequest
      const paymentMethod = getMethod(withdrawal.network, withdrawal.token)
      const isFiatWithdrawal = !!withdrawal.receipt?.FiatReceipt
      const fiat = withdrawal?.receipt?.FiatReceipt
      const finalAmount = fiat?.finalAmount
      const currency: 'EUR' | 'USD' = !fiat?.transferMethod || fiat.transferMethod.toLowerCase() === 'sepa' ? 'EUR' : 'USD'
      const fiatStartAmount = (Number(withdrawal.withdrawalAmount) / (1 - Number(fiat?.percentFee || '0'))) + Number(fiat?.flatFee || '0')
      const amount = Number(isFiatWithdrawal ? fiatStartAmount.toString() : withdrawal.withdrawalAmount)
      const amountString = roundAssetAmount(paymentMethod.ticker, amount)
      const evm = withdrawal?.receipt?.EVMReceipt
      const dot = withdrawal?.receipt?.DOTReceipt
      const btc = withdrawal?.receipt?.BTCReceipt
      const sol = withdrawal?.receipt?.SOLReceipt
      const networkFeeAmount =
        evm?.networkFeeAmount ||
        dot?.networkFeeAmount ||
        btc?.networkFeeAmount ||
        sol?.networkFeeAmount
      const feeAmount = isFiatWithdrawal ? (fiatStartAmount - Number(withdrawal.withdrawalAmount)) : (networkFeeAmount || '0')
      return {
        fee: {
          amount: roundAssetAmount(paymentMethod.ticker, Number(feeAmount)),
          ticker: paymentMethod.ticker
        },
        in: {
          amount: `- ${amountString}`,
          ticker: paymentMethod.ticker,
          color: MONEY_OUT_COLOR
        },
        ...(isFiatWithdrawal && finalAmount
          ? {
            out: {
              amount: roundAssetAmount(currency, Number(finalAmount)),
              ticker: currency,
              color: CONVERSION_COLOR
            }
          }
          : {})
      }
    }
    case TransactionKind.Payment: {
      const payment = transaction.payload as ManagedEventWithRelatedCustomer
      const managedPaymentEvent = parseManagedEvent(payment) as ManagedPaymentEvent
      const isFiatPayment = !!payment.amountsBreakdown?.onrampFee
      const feeTicker = isFiatPayment ? getMethod(payment.amountsBreakdown?.onrampNetwork || 'Ethereum', payment.amountsBreakdown?.onrampToken).ticker : managedPaymentEvent.paymentMethod.ticker
      const amount = Number(managedPaymentEvent.amount)
      const cryptoFee =
          payment.eventData?.payment?.paymentSummary?.feeAmount ??
          payment.eventData?.openBanking?.paymentSummary?.feeAmount ??
          payment.eventData?.recurringPayment?.paymentSummary?.feeAmount ??
          payment.eventData?.subscriptionPayment?.paymentSummary?.feeAmount ??
          payment.eventData?.incompletePayment?.paymentSummary?.feeAmount ??
          '0'
      const cryptoFeeCurrency =
          payment.eventData?.payment?.paymentSummary?.currency ??
          payment.eventData?.openBanking?.paymentSummary?.currency ??
          payment.eventData?.recurringPayment?.paymentSummary?.currency ??
          payment.eventData?.subscriptionPayment?.paymentSummary?.currency ??
          payment.eventData?.incompletePayment?.paymentSummary?.currency ?? 'USD'
      const conversionRates = payment.eventData?.payment?.conversionRates ??
          payment.eventData?.openBanking?.conversionRates ??
          payment.eventData?.recurringPayment?.conversionRates ??
          payment.eventData?.subscriptionPayment?.conversionRates ??
          payment.eventData?.incompletePayment?.conversionRates ?? []
      let finalCryptoFeeNumber: number = 0
      if (!isFiatPayment) {
        if (cryptoFeeCurrency === feeTicker) {
          finalCryptoFeeNumber = Number(cryptoFee)
        } else {
          const conversionRate = conversionRates.find(item => item.from === cryptoFeeCurrency && item.to === feeTicker)
          finalCryptoFeeNumber = conversionRate
            ? Number(cryptoFee) * Number(conversionRate.rate)
            : 0
        }
      }
      const amountString = roundAssetAmount(managedPaymentEvent.paymentMethod.ticker, (amount + finalCryptoFeeNumber))
      const outPaymentMethod = getMethod(payment.amountsBreakdown?.onrampNetwork || 'Ethereum', payment.amountsBreakdown?.onrampToken)
      const outAmount = Number(payment.amountsBreakdown?.onrampGrossAmount ?? '0')
      const outAmountString = roundAssetAmount(outPaymentMethod.ticker, outAmount)
      const feeAmountNumber = isFiatPayment ? Number(payment.amountsBreakdown?.onrampFee ?? '0') : finalCryptoFeeNumber

      return {
        fee: {
          amount: roundAssetAmount(feeTicker, feeAmountNumber),
          ticker: feeTicker
        },
        in: {
          amount: isFiatPayment ? amountString : `+ ${amountString}`,
          ticker: managedPaymentEvent.paymentMethod.ticker,
          color: isFiatPayment ? CONVERSION_COLOR : MONEY_IN_COLOR
        },
        ...(isFiatPayment
          ? {
            out: {
              amount: `+ ${outAmountString}`,
              ticker: outPaymentMethod.ticker,
              color: MONEY_IN_COLOR
            }
          }
          : {})
      }
    }
    case TransactionKind.Refund: {
      const refund = transaction.payload as RefundEntityWithRelatedCustomer
      const paymentMethod = getMethod(
        refund.network,
        refund.token)
      const amount = Number(refund.amount)
      const amountString = roundAssetAmount(paymentMethod.ticker, amount)

      return {
        fee: {
          amount: '',
          ticker: ''
        },
        in: {
          amount: `- ${amountString}`,
          ticker: paymentMethod.ticker,
          color: MONEY_OUT_COLOR
        }
      }
    }
    case TransactionKind.Credit:
    case TransactionKind.Debit: {
      const adjustment = transaction.payload as BalanceAdjustmentTransaction
      const method = getMethod(adjustment.network, adjustment.token)
      const amountString = roundAssetAmount(method.ticker, Number(adjustment.amount))
      const isCredit = kind === TransactionKind.Credit
      return {
        fee: {
          amount: roundAssetAmount(method.ticker, 0),
          ticker: method.ticker
        },
        in: {
          amount: `${isCredit ? '+' : '-'} ${amountString}`,
          ticker: method.ticker,
          color: isCredit ? MONEY_IN_COLOR : MONEY_OUT_COLOR
        }
      }
    }
    case TransactionKind.Swap: {
      const swap = transaction.payload as Swap
      const fromMethod = getMethod(swap.fromNetwork, swap.fromToken)
      const toMethod = getMethod(swap.toNetwork, swap.toToken)
      const fromAmount = Number(swap.fromAmount)
      const fromString = roundAssetAmount(fromMethod.ticker, fromAmount)
      const toAmount = swap.toAmount ? Number(swap.toAmount) : null
      const toString = toAmount !== null ? roundAssetAmount(toMethod.ticker, toAmount) : '—'
      const feeAmount = swap.swapFee ? Number(swap.swapFee) : 0
      return {
        fee: {
          amount: roundAssetAmount(fromMethod.ticker, feeAmount),
          ticker: fromMethod.ticker
        },
        in: {
          amount: `- ${fromString}`,
          ticker: fromMethod.ticker,
          color: MONEY_OUT_COLOR
        },
        out: {
          amount: `+ ${toString}`,
          ticker: toMethod.ticker,
          color: MONEY_IN_COLOR
        }
      }
    }
    case TransactionKind.Payout: {
      const payout = transaction.payload as Payout
      const isFiat = payout.fiatEntries && payout.fiatEntries.length > 0
      const firstCrypto = payout.cryptoEntries?.[0]
      const paymentMethod = firstCrypto
        ? getMethod(firstCrypto.network, firstCrypto.token)
        : getMethod('', undefined)

      if (isFiat) {
        const fiatAmount = Number(payout.fiatEntries[0]?.amount || payout.totalAmountUsd || payout.totalPayoutUsd)
        const fiatTicker = payout.fiatEntries[0]?.currency || 'USD'
        const fundingTicker = payout.fundingCurrency ?? 'USDC'
        const fundedAmount = Number(payout.totalAmountUsd || payout.totalPayoutUsd)

        return {
          fee: {
            amount: roundAssetAmount(fundingTicker, Number(payout.actualFeeUsd ?? payout.estimatedFeeUsd)),
            ticker: fundingTicker
          },
          in: {
            amount: `- ${roundAssetAmount(fundingTicker, fundedAmount)}`,
            ticker: fundingTicker,
            color: MONEY_OUT_COLOR
          },
          out: {
            amount: `${roundAssetAmount(fiatTicker, fiatAmount)}`,
            ticker: fiatTicker,
            color: RADOM_COLORS.NEW_BLUE
          }
        }
      }

      const fundingCurrency = firstCrypto?.fundingCurrency
      if (fundingCurrency) {
        const fundedAmount = Number(payout.totalAmountUsd || payout.totalPayoutUsd)
        const destinationAmount = (payout.cryptoEntries ?? [])
          .reduce((sum, entry) => sum + Number(entry.amount), 0)

        return {
          fee: {
            amount: roundAssetAmount(fundingCurrency, Number(payout.actualFeeUsd ?? payout.estimatedFeeUsd)),
            ticker: fundingCurrency
          },
          in: {
            amount: `- ${roundAssetAmount(fundingCurrency, fundedAmount)}`,
            ticker: fundingCurrency,
            color: MONEY_OUT_COLOR
          },
          out: {
            amount: `${roundAssetAmount(paymentMethod.ticker, destinationAmount)}`,
            ticker: paymentMethod.ticker,
            color: RADOM_COLORS.NEW_BLUE
          }
        }
      }

      const destinationAmount = (payout.cryptoEntries ?? [])
        .reduce((sum, entry) => sum + Number(entry.amount), 0)
      const hasCryptoEntries = (payout.cryptoEntries?.length ?? 0) > 0
      const ticker = hasCryptoEntries ? paymentMethod.ticker : 'USD'
      const amount = hasCryptoEntries
        ? destinationAmount
        : Number(payout.totalAmountUsd || payout.totalPayoutUsd)
      const amountString = roundAssetAmount(ticker, amount)

      return {
        fee: {
          amount: roundAssetAmount('USD', Number(payout.actualFeeUsd ?? payout.estimatedFeeUsd)),
          ticker: 'USD'
        },
        in: {
          amount: `- ${amountString}`,
          ticker,
          color: MONEY_OUT_COLOR
        }
      }
    }
    }
    const deposit = transaction.payload as Deposit
    if (deposit.depositMethod.Crypto) {
      const paymentMethod = getMethod(
        deposit.depositMethod.Crypto.network,
        deposit.depositMethod.Crypto.token)
      const amount = Number(deposit.grossAmount)
      const amountString = roundAssetAmount(paymentMethod.ticker, amount)

      return {
        fee: {
          amount: roundAssetAmount(paymentMethod.ticker, Number(deposit.fee)),
          ticker: paymentMethod.ticker
        },
        in: {
          amount: `+ ${amountString}`,
          ticker: paymentMethod.ticker,
          color: MONEY_IN_COLOR
        }
      }
    }
    const paymentMethodOut = getMethod(
      deposit.depositMethod.Fiat?.onrampNetwork || '',
      deposit.depositMethod.Fiat?.onrampToken)
    const paymentMethodIn = getMethod(
      'Fiat',
      deposit.depositMethod.Fiat?.fiatCurrency)
    const amount = Number(deposit.netAmount)
    const amountString = roundAssetAmount(paymentMethodOut.ticker, amount)
    return {
      fee: {
        amount: roundAssetAmount(paymentMethodOut.ticker, Number(deposit.fee)),
        ticker: paymentMethodOut.ticker
      },
      in: {
        amount: roundAssetAmount(paymentMethodIn.ticker, Number(deposit.fiatGrossAmount ?? '0')),
        ticker: paymentMethodIn.ticker,
        color: CONVERSION_COLOR
      },
      out: {
        amount: `+ ${amountString}`,
        ticker: paymentMethodOut.ticker,
        color: MONEY_IN_COLOR
      }
    }
  }

  const getTransactionRelated = (transaction: Transaction): { name: string, url: string } => {
    const kind = transaction.kind
    switch (kind) {
    case TransactionKind.Withdrawal: {
      const withdrawal = transaction.payload as WithdrawalRequestWithAccountName
      const text = withdrawal.withdrawalAccountName ?? withdrawal.withdrawalAddress
      return { name: text.length > 18 ? cutString(text, 18) : text, url: '/accounts' }
    }
    case TransactionKind.Payment: {
      const payment = transaction.payload as ManagedEventWithRelatedCustomer
      const text = payment.relatedCustomer?.name ?? payment.relatedCustomer?.email ?? ''
      return { name: text.length > 18 ? cutString(text, 18) : text, url: payment.relatedCustomer?.id ? `/customer/${payment.relatedCustomer?.id}` : '' }
    }
    case TransactionKind.Refund: {
      const refund = transaction.payload as RefundEntityWithRelatedCustomer
      const text = refund.relatedCustomer?.name ?? refund.relatedCustomer?.email ?? ''
      return { name: text.length > 18 ? cutString(text, 18) : text, url: refund.relatedCustomer?.id ? `/customer/${refund.relatedCustomer?.id}` : '' }
    }
    case TransactionKind.Swap: {
      const swap = transaction.payload as Swap
      const fromMethod = getMethod(swap.fromNetwork, swap.fromToken)
      const toMethod = getMethod(swap.toNetwork, swap.toToken)
      return { name: `${fromMethod.ticker} → ${toMethod.ticker}`, url: '' }
    }
    case TransactionKind.Credit:
    case TransactionKind.Debit: {
      return { name: 'Radom', url: '' }
    }
    case TransactionKind.Payout: {
      const payout = transaction.payload as Payout
      const entryCount = payout.entryCount ||
       (payout.cryptoEntries?.length || 0) + (payout.fiatEntries?.length || 0) || payout.payouts?.length || 0
      if (entryCount <= 1) {
        const beneficiary = payout.fiatEntries?.[0]?.beneficiaryName ||
          (payout.cryptoEntries?.[0]?.payoutAddress ? shortAddress(payout.cryptoEntries[0].payoutAddress, 6) : '')
        return { name: beneficiary || '1 Recipient', url: '' }
      }
      return { name: `${entryCount} Recipients`, url: '' }
    }
    }
    const deposit = transaction.payload as Deposit
    const text = deposit.depositAccountName
    return { name: text.length > 18 ? cutString(text, 18) : text, url: '/deposit_addresses' }
  }

  const isFiatDepositTransaction = (transaction: Transaction): boolean =>
    transaction.kind === TransactionKind.Deposit &&
    !!(transaction.payload as Deposit).depositMethod.Fiat

  const isFiatWithdrawalTransaction = (
    transaction: Transaction,
    supplements: TransactionDrawerSupplements = {}
  ): boolean => {
    if (transaction.kind === TransactionKind.Withdrawal) {
      const withdrawal = transaction.payload as WithdrawalRequest
      return !!withdrawal.receipt?.FiatReceipt || !!supplements.withdrawalEventData?.destination
    }

    if (transaction.kind === TransactionKind.Refund) {
      return !!supplements.withdrawalEventData?.destination || !!supplements.withdrawalEventData?.receipt?.FiatReceipt
    }

    return false
  }

  const formatRailLabel = (value?: string): string | undefined => {
    const text = normalizeDetailText(value)
    if (!text) return undefined
    const normalized = text.toLowerCase().replace(/[\s-]+/g, '_')
    const mappedLabel = FIAT_RAIL_LABELS[normalized]
    if (mappedLabel) return mappedLabel

    return text
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(part => part.length <= 4 ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  }

  const formatFiatRailLabel = (...values: Array<string | undefined>): string | undefined => {
    const meaningfulRail = values.find(value => {
      const text = normalizeDetailText(value)
      if (!text) return false
      return !NON_FIAT_RAIL_VALUES.has(text.toLowerCase().replace(/[\s-]+/g, '_'))
    })

    return formatRailLabel(meaningfulRail)
  }

  const inferDepositRailLabel = (details?: DepositBeneficiaryDetails): string | undefined => {
    const explicitRail = formatFiatRailLabel(details?.payment_rail, details?.payment_scheme)
    if (explicitRail) return explicitRail
    if (!details) return undefined
    if (details.reference || details.iban || details.bic) return 'SEPA'
    if (details.description || details.ach_memo || details.trace_number || details.sender_bank_routing_number) return 'ACH'
    if (details.imad || details.wire_message || details.uetr) return 'Wire'
    return undefined
  }

  const getWithdrawalRailLabel = (
    data?: ManagedEventWithdrawalData,
    receipt?: WithdrawalRequest['receipt'],
    account?: WithdrawalAccount
  ): string => {
    const explicitRail = formatFiatRailLabel(
      receipt?.FiatReceipt?.transferMethod,
      data?.destination?.paymentRail
    )
    if (explicitRail) return explicitRail

    const fiatAccount = account?.accountDetails.Fiat?.accountType
    if (fiatAccount?.iban) return 'SEPA'
    if (fiatAccount?.us) return 'ACH or Wire'
    return 'Bank transfer'
  }

  const getDepositBankPrimaryReference = (
    details?: DepositBeneficiaryDetails
  ): { value: string, label: string, truncateLength: number } | undefined => {
    if (!details) return undefined

    const candidates: Array<{ value?: string, label: string, truncateLength: number }> = [
      { value: details.reference, label: 'SEPA reference', truncateLength: 10 },
      { value: details.payment_reference, label: 'Payment reference', truncateLength: 10 },
      { value: details.description ?? details.ach_memo, label: 'ACH memo', truncateLength: 10 },
      { value: details.trace_number, label: 'Trace ID', truncateLength: 10 },
      { value: details.uetr, label: 'UETR', truncateLength: 8 },
      { value: details.end_to_end_id, label: 'End-to-end ID', truncateLength: 10 },
      { value: details.imad, label: 'IMAD', truncateLength: 8 },
      { value: details.sender_iban ?? details.originator_iban ?? details.iban, label: 'Sender IBAN', truncateLength: 8 },
      { value: details.iban_last_4 ? `IBAN ending ${details.iban_last_4}` : undefined, label: 'Sender IBAN', truncateLength: 14 }
    ]

    const match = candidates.find(candidate => normalizeDetailText(candidate.value))
    if (!match?.value) return undefined
    return {
      value: match.value,
      label: match.label,
      truncateLength: match.truncateLength
    }
  }

  const getFiatWithdrawalPrimaryReference = (
    transaction: Transaction,
    supplements: TransactionDrawerSupplements
  ): { value: string, label: string, truncateLength: number } => {
    const destination = supplements.withdrawalEventData?.destination
    const refund = transaction.kind === TransactionKind.Refund
      ? transaction.payload as RefundEntityWithRelatedCustomer
      : undefined
    const candidates: Array<{ value?: string, label: string, truncateLength: number }> = [
      { value: destination?.traceNumber, label: 'Trace ID', truncateLength: 10 },
      { value: destination?.uetr, label: 'UETR', truncateLength: 8 },
      { value: destination?.externalAccountId, label: 'External account', truncateLength: 8 },
      { value: refund?.withdrawalId, label: 'Withdrawal ID', truncateLength: 8 },
      { value: transaction.id, label: transaction.kind === TransactionKind.Refund ? 'Refund ID' : 'Withdrawal ID', truncateLength: 8 }
    ]

    const match = candidates.find(candidate => normalizeDetailText(candidate.value))
    return {
      value: match?.value ?? transaction.id,
      label: match?.label ?? 'ID',
      truncateLength: match?.truncateLength ?? 8
    }
  }

  const getPaymentEventData = (payment: ManagedEventWithRelatedCustomer): ManagedEventPaymentData | undefined => {
    switch (payment.eventType) {
    case 'openBanking':
      return payment.eventData.openBanking
    case 'recurringPayment':
      return payment.eventData.recurringPayment
    case 'subscriptionPayment':
      return payment.eventData.subscriptionPayment
    case 'incompletePayment':
      return payment.eventData.incompletePayment
    default:
      return payment.eventData.payment
    }
  }

  const getTransactionHashLinks = (
    transaction: Transaction,
    supplements: TransactionDrawerSupplements = {}
  ): Array<{ hash: string, network?: string }> => {
    const payload: any = transaction.payload
    const seen = new Set<string>()
    const result: Array<{ hash: string, network?: string }> = []

    const add = (hash?: string, network?: string): void => {
      const h = hash?.trim()
      if (!h || seen.has(h)) return
      seen.add(h)
      result.push({ hash: h, network })
    }

    const addReceiptHashes = (receipt?: any, defaultNetwork?: string): void => {
      if (receipt?.EVMReceipt?.transactionHash) add(receipt.EVMReceipt.transactionHash, defaultNetwork)
      if (receipt?.DOTReceipt?.transactionHash) add(receipt.DOTReceipt.transactionHash, defaultNetwork)
      if (receipt?.BTCReceipt?.transactionId) add(receipt.BTCReceipt.transactionId, defaultNetwork ?? 'Bitcoin')
      if (receipt?.BSVReceipt?.transactionId) add(receipt.BSVReceipt.transactionId, 'BitcoinSV')
      if (receipt?.DOGEReceipt?.transactionId) add(receipt.DOGEReceipt.transactionId, 'Dogecoin')
      if (receipt?.LTCReceipt?.transactionId) add(receipt.LTCReceipt.transactionId, 'Litecoin')
      if (receipt?.BCHReceipt?.transactionId) add(receipt.BCHReceipt.transactionId, 'BitcoinCash')
      if (receipt?.XMRReceipt?.transactionId) add(receipt.XMRReceipt.transactionId, undefined)
      if (receipt?.SOLReceipt?.signature) add(receipt.SOLReceipt.signature, defaultNetwork ?? 'Solana')
    }

    if (transaction.kind === TransactionKind.Payout) {
      payload.cryptoEntries?.forEach((entry: any) => add(entry.txHash, entry.network))
      payload.payouts?.forEach((entry: any) => add(entry.payoutEntryReceipt?.crypto?.txHash, entry.network))
    }

    if (transaction.kind === TransactionKind.Withdrawal) {
      addReceiptHashes(payload.receipt, payload.network)
      addReceiptHashes(supplements.withdrawalEventData?.receipt, payload.network)
    }

    if (transaction.kind === TransactionKind.Deposit) {
      const network = payload.depositMethod?.Crypto?.network
      add(payload.transactionHash, network)
      add(payload.txHash, network)
      add(payload.receipt?.transactionHash, network)
      add(payload.receipt?.txHash, network)
      add(payload.depositTransaction?.transactionHash, network)
      add(supplements.depositEventData?.transactionHash, network)
    }

    if (transaction.kind === TransactionKind.Payment) {
      const paymentData = getPaymentEventData(payload as ManagedEventWithRelatedCustomer)
      const network = paymentData?.paymentMethod?.network
      const paymentNet = payload.eventData?.payment?.paymentMethod?.network ?? network
      const obNet = payload.eventData?.openBanking?.paymentMethod?.network ?? network
      const recurNet = payload.eventData?.recurringPayment?.paymentMethod?.network ?? network
      const subNet = payload.eventData?.subscriptionPayment?.paymentMethod?.network ?? network
      const incNet = payload.eventData?.incompletePayment?.paymentMethod?.network ?? network
      add(payload.transactionHash, network)
      add(payload.eventData?.payment?.transactionHash, paymentNet)
      add(payload.eventData?.openBanking?.transactionHash, obNet)
      add(payload.eventData?.recurringPayment?.transactionHash, recurNet)
      add(payload.eventData?.subscriptionPayment?.transactionHash, subNet)
      add(payload.eventData?.incompletePayment?.transactionHash, incNet)
      payload.transactionHashes?.forEach((hash: string) => add(hash, network))
      paymentData?.transactions?.forEach((tx: ManagedEventTransaction) => add(tx.transactionHash, tx.network))
      payload.eventData?.payment?.transactionHashes?.forEach((hash: string) => add(hash, paymentNet))
      payload.eventData?.openBanking?.transactionHashes?.forEach((hash: string) => add(hash, obNet))
      payload.eventData?.recurringPayment?.transactionHashes?.forEach((hash: string) => add(hash, recurNet))
      payload.eventData?.subscriptionPayment?.transactionHashes?.forEach((hash: string) => add(hash, subNet))
      payload.eventData?.incompletePayment?.transactionHashes?.forEach((hash: string) => add(hash, incNet))
    }

    if (transaction.kind === TransactionKind.Refund) {
      const refund = payload as RefundEntityWithRelatedCustomer
      add(payload.transactionHash, refund.network)
      add(payload.receipt?.transactionHash, refund.network)
      add(payload.payoutEntryReceipt?.crypto?.txHash, refund.network)
      addReceiptHashes(supplements.withdrawalEventData?.receipt, refund.network)
    }

    if (transaction.kind === TransactionKind.Swap) {
      add(payload.fromTxid, payload.fromNetwork)
      add(payload.toTxid, payload.toNetwork)
    }

    return result
  }

  const getTransactionHashes = (
    transaction: Transaction,
    supplements: TransactionDrawerSupplements = {}
  ): string[] => getTransactionHashLinks(transaction, supplements).map(item => item.hash)

  const getTransactionSearchText = (transaction: ExtendedTransaction): string => {
    const hashes = getTransactionHashes(transaction)
    const payload: any = transaction.payload
    const parts = [
      transaction.id,
      transaction.kind,
      transaction.related.name,
      transaction.summary.in.ticker,
      transaction.summary.out?.ticker,
      payload.depositAccountName,
      payload.withdrawalAddress,
      payload.withdrawalAccountName,
      payload.relatedCustomer?.name,
      payload.relatedCustomer?.email,
      payload.payoutAddress,
      payload.withdrawalId,
      payload.ivyOrderId,
      payload.associatedEntityId,
      ...hashes
    ]

    return parts.filter(Boolean).join(' ').toLowerCase()
  }

  const filteredTransactions = extendedTransactions
  // Detected, unconfirmed inbound transactions sit at the top of the first
  // page as ordinary rows. They follow the type filter, count as pending for
  // the status filter, and step aside while a search is running.
  const visibleDetected: DetectedRow[] = curPage === 0 &&
    transactionSearch.trim().length === 0 &&
    transactionStatuses.includes(TransactionStatus.Pending)
    ? detectedRows.filter(row =>
      transactionKinds.includes(row.kind === 'deposit' ? TransactionKind.Deposit : TransactionKind.Payment))
    : []
  const selectedDetected = detectedRows.find(row => row.id === selectedDetectedId)
  const allKinds = Object.values(TransactionKind)
  const allStatuses = Object.values(TransactionStatus)
  const sameMembers = <T extends string>(left: T[], right: T[]): boolean =>
    left.length === right.length && left.every(item => right.includes(item))
  const toggleTransactionKind = (kind: TransactionKind): void => {
    setTransactionKinds(prev => prev.includes(kind)
      ? prev.length === 1 ? prev : prev.filter(item => item !== kind)
      : [...prev, kind]
    )
  }
  const toggleTransactionStatus = (status: TransactionStatus): void => {
    setTransactionStatuses(prev => prev.includes(status)
      ? prev.length === 1 ? prev : prev.filter(item => item !== status)
      : [...prev, status]
    )
  }
  const resetFilters = (): void => {
    setTransactionKinds(allKinds)
    setTransactionStatuses(allStatuses)
    setTransactionSearch('')
    setSelectedTransactionId(undefined)
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.delete('q')
      next.delete('selected')
      return next
    }, { replace: true })
  }
  const selectedKindPreset = TRANSACTION_KIND_PRESETS.find(preset =>
    sameMembers(transactionKinds, preset.kinds))
  const selectedStatusPreset = TRANSACTION_STATUS_PRESETS.find(preset =>
    sameMembers(transactionStatuses, preset.statuses))
  const hasActiveFilters =
    transactionSearch.trim().length > 0 ||
    !sameMembers(transactionKinds, allKinds) ||
    !sameMembers(transactionStatuses, allStatuses)
  const transactionKindFilterLabel = selectedKindPreset?.label ??
    `${transactionKinds.length} type${transactionKinds.length === 1 ? '' : 's'}`
  const transactionStatusFilterLabel = selectedStatusPreset?.label ??
    `${transactionStatuses.length} status${transactionStatuses.length === 1 ? '' : 'es'}`

  const selectedTransaction = useMemo(() => {
    if (!selectedTransactionId) return undefined
    return extendedTransactions.find(transaction => transaction.id === selectedTransactionId)
  }, [extendedTransactions, selectedTransactionId])

  const isIncompletePayment = useMemo(() =>
    selectedTransaction?.kind === TransactionKind.Payment &&
    selectedTransaction?.status === TransactionStatus.Incomplete,
  [selectedTransaction])

  // Open-banking payments are refundable straight from the drawer: the event
  // links its checkout session, which the shared RefundForm operates on.
  // Completed open-banking payments land as regular `payment` events with a
  // Fiat payment method, so both event-data slots are checked.
  const refundableCheckoutSessionId = useMemo(() => {
    if (selectedTransaction?.kind !== TransactionKind.Payment ||
      selectedTransaction.status !== TransactionStatus.Successful) {
      return undefined
    }
    const payload = selectedTransaction.payload as ManagedEventWithRelatedCustomer
    const entry = payload.eventData?.openBanking ?? payload.eventData?.payment
    if (!entry) {
      return undefined
    }
    const isOpenBanking = !!payload.eventData?.openBanking ||
      entry.paymentMethod?.network === 'Fiat' ||
      !!entry.openBankingPaymentData
    if (!isOpenBanking) {
      return undefined
    }
    return entry.radomData?.checkoutSession?.checkoutSessionId
  }, [selectedTransaction])

  // The checkout session of a bank payment: its full story (what happened at
  // the bank, the references, the refunds) lives in the bank payment drawer.
  const bankPaymentCheckoutSessionId = useMemo(() => {
    if (selectedTransaction?.kind !== TransactionKind.Payment) {
      return undefined
    }
    const payload = selectedTransaction.payload as ManagedEventWithRelatedCustomer
    const entry = payload.eventData?.openBanking ?? payload.eventData?.payment
    if (!entry || (!payload.eventData?.openBanking && !entry.openBankingPaymentData)) {
      return undefined
    }
    return entry.radomData?.checkoutSession?.checkoutSessionId
  }, [selectedTransaction])

  const openRefundForm = async (): Promise<void> => {
    if (!refundableCheckoutSessionId) return
    setIsLoadingRefundSession(true)
    try {
      const session = await Radom.getCheckoutSession(refundableCheckoutSessionId)
      setRefundSession(session)
      // The drawer overlay sits above the modal layer, so hand off to the
      // modal cleanly rather than stacking them.
      selectTransaction(undefined)
    } catch (err) {
      errorToast(err?.response?.data?.error || err.message || 'Failed to load the payment for refunding')
    } finally {
      setIsLoadingRefundSession(false)
    }
  }

  const selectTransaction = (id?: string): void => {
    setSelectedTransactionId(id)
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (id) {
        next.set('selected', id)
      } else {
        next.delete('selected')
      }
      return next
    }, { replace: true })
  }

  useEffect(() => {
    if (hasAppliedInitialQueryParams) return

    if (transactionQueryParam) {
      setTransactionSearch(transactionQueryParam)
    }

    if (selectedTransactionParam) {
      setSelectedTransactionId(selectedTransactionParam)
    }

    setHasAppliedInitialQueryParams(true)
  }, [hasAppliedInitialQueryParams, selectedTransactionParam, transactionQueryParam])

  useEffect(() => {
    if (!detectedParam || appliedDetectedParam.current === detectedParam) return
    if (detectedRows.some(row => row.id === detectedParam)) {
      appliedDetectedParam.current = detectedParam
      setSelectedDetectedId(detectedParam)
    }
  }, [detectedParam, detectedRows])

  useEffect(() => {
    if (!selectedTransactionParam || extendedTransactions.length === 0) return

    const normalizedSelectedParam = selectedTransactionParam.toLowerCase()
    const matchedTransaction = extendedTransactions.find(transaction =>
      transaction.id === selectedTransactionParam ||
      getTransactionSearchText(transaction).includes(normalizedSelectedParam)
    )

    if (matchedTransaction && selectedTransactionId !== matchedTransaction.id) {
      setSelectedTransactionId(matchedTransaction.id)
    }
  }, [extendedTransactions, selectedTransactionId, selectedTransactionParam])

  useEffect(() => {
    setSelectedDepositEventData(undefined)
    setSelectedWithdrawalEventData(undefined)
    setSelectedWithdrawalAccount(undefined)

    if (!selectedTransaction) {
      setIsDrawerSupplementLoading(false)
      return
    }

    let isMounted = true
    const loadDrawerSupplement = async (): Promise<void> => {
      const shouldLoadDeposit = selectedTransaction.kind === TransactionKind.Deposit
      const shouldLoadWithdrawal = selectedTransaction.kind === TransactionKind.Withdrawal
      const shouldLoadRefundWithdrawal = selectedTransaction.kind === TransactionKind.Refund

      if (!shouldLoadDeposit && !shouldLoadWithdrawal && !shouldLoadRefundWithdrawal) {
        setIsDrawerSupplementLoading(false)
        return
      }

      setIsDrawerSupplementLoading(true)

      try {
        if (shouldLoadDeposit) {
          const event = await Radom.getDepositEvent(selectedTransaction.id)
          if (isMounted) {
            setSelectedDepositEventData(event.eventData.deposit)
          }
        } else if (shouldLoadWithdrawal) {
          const withdrawal = selectedTransaction.payload as WithdrawalRequest
          const accountPromise = withdrawal.withdrawalAccountId
            ? Radom.getWithdrawalAccount(withdrawal.withdrawalAccountId)
            : Promise.resolve(undefined)
          const [eventResult, accountResult] = await Promise.allSettled([
            Radom.getWithdrawalEvent(selectedTransaction.id),
            accountPromise
          ])

          if (!isMounted) return

          if (eventResult.status === 'fulfilled') {
            setSelectedWithdrawalEventData(eventResult.value.eventData.withdrawal ?? eventResult.value.eventData.refund)
          }
          if (accountResult.status === 'fulfilled') {
            setSelectedWithdrawalAccount(accountResult.value)
          }
        } else {
          // A refund's settlement is recorded as a Refund event keyed by the
          // refund id; there is no Withdrawal event for it.
          const event = await Radom.getRefundEvent(selectedTransaction.id)
          if (isMounted) {
            setSelectedWithdrawalEventData(event.eventData.refund ?? event.eventData.withdrawal)
          }
        }
      } catch (e) {
        console.warn('Failed to load transaction drawer details', e)
      } finally {
        if (isMounted) {
          setIsDrawerSupplementLoading(false)
        }
      }
    }

    void loadDrawerSupplement()

    return () => {
      isMounted = false
    }
  }, [selectedTransaction?.id, selectedTransaction?.kind, selectedTransaction?.payload])

  const formatDrawerDate = (value?: string): string => {
    if (!value) return '-'
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(value))
  }

  const normalizeDetailText = (value?: string | number | null): string | undefined => {
    if (value === undefined || value === null) return undefined
    const text = String(value).trim()
    return text.length > 0 ? text : undefined
  }

  const coalesceDetailText = (...values: Array<string | number | null | undefined>): string | undefined =>
    values.map(normalizeDetailText).find(Boolean)

  const renderCopyValue = (
    value?: string | number | null,
    options: { truncateLength?: number, mono?: boolean } = {}
  ): React.ReactNode => {
    const text = normalizeDetailText(value)
    if (!text) return '-'
    const displayValue = options.truncateLength ? shortAddress(text, options.truncateLength) : text
    const ValueComponent = options.mono === false ? 'span' : DrawerMono
    return <DrawerReferenceInline>
      <ValueComponent>{displayValue}</ValueComponent>
      <CopyButton text={text} />
    </DrawerReferenceInline>
  }

  const renderCopyReference = (value?: string, truncateLength = 10): React.ReactNode =>
    renderCopyValue(value, { truncateLength, mono: true })

  const renderNameAndSecondary = (primary?: string, secondary?: string): React.ReactNode => {
    const normalizedPrimary = normalizeDetailText(primary)
    const normalizedSecondary = normalizeDetailText(secondary)
    if (!normalizedPrimary && !normalizedSecondary) return '-'
    return <DrawerValueStack>
      {normalizedPrimary && <span>{normalizedPrimary}</span>}
      {normalizedSecondary && <DrawerSecondaryValue>{normalizedSecondary}</DrawerSecondaryValue>}
    </DrawerValueStack>
  }

  const addTextDetailRow = (
    rows: DrawerDetailConfig[],
    label: string,
    value?: string | number | null,
    options: { copy?: boolean, truncateLength?: number, mono?: boolean } = {}
  ): void => {
    const text = normalizeDetailText(value)
    if (!text) return
    rows.push({
      label,
      value: options.copy
        ? renderCopyValue(text, { truncateLength: options.truncateLength, mono: options.mono ?? true })
        : text
    })
  }

  const addNodeDetailRow = (
    rows: DrawerDetailConfig[],
    label: string,
    value: React.ReactNode
  ): void => {
    if (value === undefined || value === null || value === '-') return
    rows.push({ label, value })
  }

  const formatAddressParts = (...parts: Array<string | undefined | null>): string | undefined =>
    normalizeDetailText(parts.map(part => normalizeDetailText(part)).filter(Boolean).join(', '))

  const getTransactionLedger = (transaction: ExtendedTransaction): ManagedBalanceLedger[] => {
    return managedBalanceLedger.filter(item => {
      if (transaction.kind === TransactionKind.Payout) {
        const payout = transaction.payload as Payout
        const fundingIds = [...(payout.fundingWithdrawalIds ?? []), ...(payout.fundingConversionIds ?? [])]
        return item.associatedId === transaction.id ||
          (!!item.associatedId && fundingIds.includes(item.associatedId))
      }

      if (transaction.kind !== TransactionKind.Payment) {
        return item.associatedId === transaction.id
      }

      const payment = transaction.payload as ManagedEventWithRelatedCustomer
      const managedPaymentEvent = parseManagedEvent(payment) as ManagedPaymentEvent
      const donationLinkId = payment.eventData?.payment?.radomData?.donationLink?.donationLinkId
      return managedPaymentEvent.associatedEntity.id === item.associatedId ||
        (!!donationLinkId && donationLinkId === item.associatedId)
    }).filter(item => {
      const balancePaymentMethod = getMethod(item.network, item.token)

      // Payouts can traverse several balances (funding debit, hedge transit legs);
      // merchants only care about the balance they funded the payout from.
      if (transaction.kind === TransactionKind.Payout) {
        return transaction.summary.in.ticker === balancePaymentMethod.ticker
      }

      let transactionCurrencyTicker: string
      if (
        transaction.kind === TransactionKind.Deposit ||
        transaction.kind === TransactionKind.Payment ||
        transaction.kind === TransactionKind.Swap
      ) {
        transactionCurrencyTicker = transaction.summary.out
          ? transaction.summary.out.ticker
          : transaction.summary.in.ticker
      } else {
        transactionCurrencyTicker = transaction.summary.in.ticker
      }

      return transactionCurrencyTicker === balancePaymentMethod.ticker
    })
  }

  // What a payout delivered out of the balance it was funded from: the
  // ledger deduction less the fee when the fee is booked in the same asset,
  // else the sum of the entries in that asset. A fee in another unit is
  // never subtracted from an asset amount.
  const payoutDelivered = (
    transaction: ExtendedTransaction,
    unified: ManagedBalanceLedger[]
  ): { amount: number, ticker: string } | null => {
    if (transaction.kind !== TransactionKind.Payout || transaction.summary.out || unified.length <= 0) return null
    const ticker = ledgerTicker(unified[0])
    const totalDeducted = unified.reduce((sum, entry) => {
      return sum + Math.abs(Number(entry.balanceBefore) - Number(entry.balanceAfter))
    }, 0)
    let amount: number
    if (transaction.summary.fee.ticker === ticker) {
      amount = totalDeducted - (Number(transaction.summary.fee.amount) || 0)
    } else {
      const payout = transaction.payload as Payout
      const entries = (payout.cryptoEntries ?? []).filter(entry => getMethod(entry.network, entry.token).ticker === ticker)
      if (entries.length === 0) return null
      amount = entries.reduce((sum, entry) => sum + Number(entry.amount), 0)
    }
    if (!(amount > 0)) return null
    return { amount, ticker }
  }

  const renderPayoutLedgerAmount = (
    transaction: ExtendedTransaction,
    unified: ManagedBalanceLedger[]
  ): React.ReactNode => {
    const delivered = payoutDelivered(transaction, unified)
    if (!delivered) return null
    return <ConversionLine>
      <Chevron style={{ opacity: 0.6, transform: 'rotate(-90deg)', width: 12, height: 12 }} />
      <span style={{ color: RADOM_COLORS.RADOM_BLUE }}>
        {roundAssetAmount(delivered.ticker, delivered.amount)}
      </span>
      <AmountTicker style={{ color: RADOM_COLORS.RADOM_BLUE }}>
        {delivered.ticker}
      </AmountTicker>
    </ConversionLine>
  }

  const getPaymentEventLabel = (eventType?: string): string => {
    switch (eventType) {
    case 'openBanking':
      return 'Open banking payment'
    case 'recurringPayment':
      return 'Recurring payment'
    case 'subscriptionPayment':
      return 'Subscription payment'
    case 'incompletePayment':
      return 'Incomplete payment'
    default:
      return 'Payment'
    }
  }
  const getPaymentDisplayLabel = (payment: ManagedEventWithRelatedCustomer): string => {
    const paymentData = getPaymentEventData(payment)
    if (payment.eventData.openBanking || paymentData?.openBankingPaymentData) {
      return 'Open banking payment'
    }
    if (paymentData?.radomData.invoice) {
      return 'Invoice payment'
    }
    if (paymentData?.radomData.paymentLink) {
      return 'Payment link payment'
    }
    if (paymentData?.radomData.checkoutSession) {
      return 'Checkout session completed'
    }
    if (paymentData?.radomData.subscription) {
      return 'Subscription payment'
    }
    return getPaymentEventLabel(payment.eventType)
  }

  const getMethodLabel = (network?: string, token?: string): string => {
    const method = getMethod(network || '', token)
    return `${method.ticker} on ${method.hostChain?.name ?? method.name}`
  }

  const appendPayerFinancialAddressRows = (
    rows: DrawerDetailConfig[],
    account: PayerFinancialAddress | undefined,
    role: 'Sender' | 'Recipient'
  ): void => {
    if (!account) return

    if (account.iban) {
      addTextDetailRow(rows, `${role} name`, account.iban.accountHolderName)
      addTextDetailRow(rows, `${role} IBAN`, account.iban.iban, { copy: true })
      addTextDetailRow(rows, `${role} BIC`, account.iban.bic, { copy: true })
    }

    if (account.sortCode) {
      addTextDetailRow(rows, `${role} name`, account.sortCode.accountHolderName)
      addTextDetailRow(rows, `${role} sort code`, account.sortCode.sortCode, { copy: true })
      addTextDetailRow(rows, `${role} account number`, account.sortCode.accountNumber, { copy: true })
    }

    if (account.bankCode) {
      addTextDetailRow(rows, `${role} name`, account.bankCode.accountHolderName)
      addTextDetailRow(rows, `${role} bank code`, account.bankCode.code, { copy: true })
      addTextDetailRow(rows, `${role} account number`, account.bankCode.accountNumber, { copy: true })
    }

    if (account.bban) {
      addTextDetailRow(rows, `${role} name`, account.bban.accountHolderName)
      addTextDetailRow(rows, `${role} BBAN`, account.bban.bban, { copy: true })
      addTextDetailRow(rows, `${role} BIC`, account.bban.bic, { copy: true })
    }
  }

  const getPayerAccountHolderName = (account?: PayerFinancialAddress): string | undefined =>
    coalesceDetailText(
      account?.iban?.accountHolderName,
      account?.sortCode?.accountHolderName,
      account?.bankCode?.accountHolderName,
      account?.bban?.accountHolderName
    )

  const getPayerBic = (account?: PayerFinancialAddress): string | undefined =>
    coalesceDetailText(account?.iban?.bic, account?.bban?.bic)

  const getPayerAccountNumber = (account?: PayerFinancialAddress): string | undefined =>
    coalesceDetailText(account?.sortCode?.accountNumber, account?.bankCode?.accountNumber)

  const appendWithdrawalAccountRows = (
    rows: DrawerDetailConfig[],
    account?: WithdrawalAccount
  ): boolean => {
    const fiatAccount = account?.accountDetails.Fiat
    if (!fiatAccount) return false

    addTextDetailRow(rows, 'Account owner type', fiatAccount.accountOwnerType)

    if (fiatAccount.accountType.iban) {
      addTextDetailRow(rows, 'Recipient IBAN', fiatAccount.accountType.iban.iban, { copy: true })
      addTextDetailRow(rows, 'Recipient BIC', fiatAccount.accountType.iban.bic, { copy: true })
    }

    if (fiatAccount.accountType.us) {
      addTextDetailRow(rows, 'Recipient account number', fiatAccount.accountType.us.accountNumber, { copy: true })
      addTextDetailRow(rows, 'Recipient routing number', fiatAccount.accountType.us.routingNumber, { copy: true })
      addTextDetailRow(rows, 'Recipient bank', fiatAccount.accountType.us.bankName)
    }

    return true
  }

  const appendDepositBeneficiaryRows = (
    rows: DrawerDetailConfig[],
    details?: DepositBeneficiaryDetails
  ): void => {
    if (!details) return

    addTextDetailRow(rows, 'Sender name', details.sender_name ?? details.originator_name)
    addTextDetailRow(rows, 'Sender IBAN', details.sender_iban ?? details.originator_iban ?? details.iban, {
      copy: true
    })
    addTextDetailRow(rows, 'Sender IBAN last 4', details.iban_last_4, { copy: true })
    addTextDetailRow(rows, 'Sender account number', details.sender_account_number, { copy: true })
    addTextDetailRow(rows, 'Sender address', details.originator_address)
    addTextDetailRow(rows, 'Sender bank', details.sender_bank_name)
    addTextDetailRow(rows, 'Sender routing number', details.sender_bank_routing_number, { copy: true })
    addTextDetailRow(rows, 'Recipient name', details.recipient_name ?? details.bank_beneficiary_name)
    addTextDetailRow(rows, 'Recipient address', details.bank_beneficiary_address)
    addTextDetailRow(rows, 'Bank name', details.bank_name)
    addTextDetailRow(rows, 'Bank routing number', details.bank_routing_number, { copy: true })
    addTextDetailRow(rows, 'BIC', details.bic, { copy: true })
    addTextDetailRow(rows, 'SEPA reference', details.reference, { copy: true, mono: false })
    addTextDetailRow(rows, 'Payment reference', details.payment_reference, { copy: true, mono: false })
    addTextDetailRow(rows, 'ACH memo', details.description ?? details.ach_memo, { copy: true, mono: false })
    addTextDetailRow(rows, 'Trace ID', details.trace_number, { copy: true })
    addTextDetailRow(rows, 'UETR', details.uetr, { copy: true })
    addTextDetailRow(rows, 'End-to-end ID', details.end_to_end_id, { copy: true })
    addTextDetailRow(rows, 'Remittance information', details.remittance_information, { copy: true, mono: false })
    addTextDetailRow(rows, 'IMAD', details.imad, { copy: true })
    addTextDetailRow(rows, 'Wire message', details.wire_message, { copy: true, mono: false })
    addTextDetailRow(rows, 'Estimated arrival', details.estimated_arrival_date)
  }

  const getDepositBeneficiaryDetails = (
    deposit: Deposit,
    eventData?: ManagedEventDepositData
  ): DepositBeneficiaryDetails | undefined =>
    eventData?.beneficiaryDetails ??
    (deposit as Deposit & { beneficiaryDetails?: DepositBeneficiaryDetails }).beneficiaryDetails

  const appendWithdrawalDestinationRows = (
    rows: DrawerDetailConfig[],
    data?: ManagedEventWithdrawalData,
    receipt?: WithdrawalRequest['receipt'],
    account?: WithdrawalAccount
  ): void => {
    const destination = data?.destination
    const hasDestinationData = !!destination || !!receipt?.FiatReceipt
    if (!hasDestinationData) return

    addTextDetailRow(rows, 'Payment rail', getWithdrawalRailLabel(data, receipt, account))
    addTextDetailRow(rows, 'Currency', destination?.currency)
    addTextDetailRow(rows, 'Trace ID', destination?.traceNumber, { copy: true })
    addTextDetailRow(rows, 'UETR', destination?.uetr, { copy: true })
    addTextDetailRow(rows, 'External account ID', destination?.externalAccountId, { copy: true, truncateLength: 8 })
  }

  const appendPaymentTransactionRows = (
    rows: DrawerDetailConfig[],
    transactions?: ManagedEventTransaction[]
  ): void => {
    const senderAddresses = new Set<string>()
    transactions?.forEach(transaction => {
      transaction.senderAddresses?.forEach(sender => {
        if (sender.address) senderAddresses.add(sender.address)
      })
    })

    Array.from(senderAddresses).forEach((address, idx) => {
      addTextDetailRow(rows, idx === 0 ? 'Sender address' : `Sender address ${idx + 1}`, address, {
        copy: true,
        truncateLength: 10
      })
    })
  }

  const getTransactionSpecificRows = (
    transaction: ExtendedTransaction,
    supplements: TransactionDrawerSupplements = {}
  ): DrawerDetailConfig[] => {
    const rows: DrawerDetailConfig[] = []

    switch (transaction.kind) {
    case TransactionKind.Withdrawal: {
      const withdrawal = transaction.payload as WithdrawalRequestWithAccountName
      const method = getMethod(withdrawal.network, withdrawal.token)
      const receipt = supplements.withdrawalEventData?.receipt ?? withdrawal.receipt
      const fiatReceipt = receipt?.FiatReceipt
      const isFiatWithdrawal = !!fiatReceipt || !!supplements.withdrawalEventData?.destination
      const finalAmount = fiatReceipt?.finalAmount
      const fiatCurrency = supplements.withdrawalEventData?.destination?.currency
      const fiatAccount = supplements.withdrawalAccount?.accountDetails.Fiat

      addTextDetailRow(
        rows,
        isFiatWithdrawal ? 'Recipient name' : 'Destination',
        fiatAccount?.accountOwnerName ?? withdrawal.withdrawalAccountName
      )
      if (isFiatWithdrawal) {
        appendWithdrawalDestinationRows(rows, supplements.withdrawalEventData, receipt, supplements.withdrawalAccount)
        const hasBankAccountRows = appendWithdrawalAccountRows(rows, supplements.withdrawalAccount)
        if (withdrawal.withdrawalAccountId !== supplements.withdrawalEventData?.destination?.externalAccountId) {
          addTextDetailRow(rows, 'Withdrawal account ID', withdrawal.withdrawalAccountId, {
            copy: true,
            truncateLength: 8
          })
        }
        addTextDetailRow(rows, 'Final amount', finalAmount ? `${finalAmount}${fiatCurrency ? ` ${fiatCurrency}` : ''}` : undefined)
        addTextDetailRow(rows, 'Flat fee', fiatReceipt?.flatFee)
        addTextDetailRow(rows, 'Percent fee', fiatReceipt?.percentFee)
        if (!hasBankAccountRows) {
          addTextDetailRow(rows, 'Bank account details', 'Not returned for this transaction')
        }
      } else {
        addTextDetailRow(rows, 'Recipient address', withdrawal.withdrawalAddress, { copy: true, truncateLength: 10 })
      }

      addTextDetailRow(rows, 'Asset', `${method.ticker} on ${method.hostChain?.name ?? method.name}`)
      addTextDetailRow(rows, 'Completed', withdrawal.completedAt ? formatDrawerDate(withdrawal.completedAt) : undefined)
      addTextDetailRow(rows, 'Failure reason', withdrawal.failureReason ?? supplements.withdrawalEventData?.failureReason)
      addTextDetailRow(rows, 'Withdrawal ID', withdrawal.id, { copy: true, truncateLength: 8 })
      return rows
    }
    case TransactionKind.Payment: {
      const payment = transaction.payload as ManagedEventWithRelatedCustomer
      const managedPaymentEvent = parseManagedEvent(payment) as ManagedPaymentEvent
      const paymentData = getPaymentEventData(payment)
      const openBankingData = paymentData?.openBankingPaymentData
      // 'CheckoutSession' reads as 'Checkout session'.
      const entityType = (AssociatedEntityType[managedPaymentEvent.associatedEntity.entityType] ?? 'Entity')
        .replace(/([a-z])([A-Z])/g, (_match: string, a: string, b: string) => `${a} ${b.toLowerCase()}`)
      // Bank payment amounts to the precision they were booked at, so the
      // converted amount less the fee is exactly what was added to the balance.
      const exactAmount = (value: string | number): string => {
        const amount = Number(value)
        return Number.isFinite(amount)
          ? amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })
          : String(value)
      }
      const primaryPaymentReference =
        openBankingData?.paymentReference ?? openBankingData?.bankStatementReference
      const payerFinancialAddress = openBankingData?.payerFinancialAddress
      const senderName = coalesceDetailText(
        openBankingData?.senderName,
        getPayerAccountHolderName(payerFinancialAddress)
      )
      const senderIban = coalesceDetailText(openBankingData?.senderIban, payerFinancialAddress?.iban?.iban)
      const senderBic = coalesceDetailText(openBankingData?.senderBic, getPayerBic(payerFinancialAddress))
      const senderSortCode = coalesceDetailText(
        openBankingData?.senderSortCode,
        payerFinancialAddress?.sortCode?.sortCode
      )
      const senderAccountNumber = coalesceDetailText(
        openBankingData?.senderAccountNumber,
        getPayerAccountNumber(payerFinancialAddress)
      )
      const senderBankCode = coalesceDetailText(openBankingData?.senderBankCode, payerFinancialAddress?.bankCode?.code)
      const senderBban = coalesceDetailText(openBankingData?.senderBban, payerFinancialAddress?.bban?.bban)

      addNodeDetailRow(rows, openBankingData ? 'Contact' : 'Sender', renderNameAndSecondary(
        payment.relatedCustomer?.name,
        payment.relatedCustomer?.email
      ))
      const entityId = managedPaymentEvent.associatedEntity.id
      const entityLink = managedPaymentEvent.associatedEntity.link
      if (entityId) {
        if (entityLink) {
          addNodeDetailRow(rows, entityType, <DrawerReferenceInline>
            <DrawerMono>{shortAddress(entityId, 8)}</DrawerMono>
            <CopyButton text={entityId} />
            <Link
              to={entityLink}
              onClick={e => e.stopPropagation()}
              style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--text-faint, #7b8494)' }}
            >
              <LinkExternal stroke='currentColor' width={13} />
            </Link>
          </DrawerReferenceInline>)
        } else {
          addTextDetailRow(rows, entityType, entityId, { copy: true, truncateLength: 8 })
        }
      }
      addTextDetailRow(rows, 'Bank', openBankingData?.bankName)
      addTextDetailRow(rows, 'Bank ID', openBankingData?.bankId, { copy: true })
      addTextDetailRow(rows, 'Bank market', openBankingData?.bankMarket)
      const bankPaymentHistory = openBankingData?.statusHistoryList ?? []
      addTextDetailRow(rows, 'Bank payment status', openBankingData?.status
        ? openBankingStatusPresentation(openBankingStatusFromProvider(
          openBankingData.status,
          bankPaymentHistory[bankPaymentHistory.length - 1]?.reason,
          false
        )).label
        : undefined)
      addTextDetailRow(rows, 'Payment scheme', openBankingData?.paymentScheme)
      addTextDetailRow(rows, 'Instant rail', openBankingData?.instantPaymentScheme === undefined
        ? undefined
        : openBankingData.instantPaymentScheme ? 'Yes' : 'No')
      addTextDetailRow(rows, 'Payment method', openBankingData
        ? `Bank transfer (${managedPaymentEvent.paymentMethod.ticker})`
        : `${managedPaymentEvent.paymentMethod.ticker} on ${
          managedPaymentEvent.paymentMethod.hostChain?.name ?? managedPaymentEvent.paymentMethod.name
        }`)
      addTextDetailRow(rows, 'Payment reference', primaryPaymentReference, {
        copy: true,
        mono: false
      })
      if (openBankingData?.bankStatementReference &&
        openBankingData.bankStatementReference !== primaryPaymentReference) {
        addTextDetailRow(rows, 'Bank statement reference', openBankingData.bankStatementReference, {
          copy: true,
          mono: false
        })
      }
      addTextDetailRow(rows, 'End-to-end ID', openBankingData?.endToEndId, { copy: true })
      addTextDetailRow(rows, 'Remittance information', openBankingData?.remittanceInformation, {
        copy: true,
        mono: false
      })
      addTextDetailRow(rows, 'Sender name', senderName)
      addTextDetailRow(rows, 'Sender IBAN', senderIban, { copy: true })
      addTextDetailRow(rows, 'Sender BIC', senderBic, { copy: true })
      addTextDetailRow(rows, 'Sender sort code', senderSortCode, { copy: true })
      addTextDetailRow(rows, 'Sender account number', senderAccountNumber, { copy: true })
      addTextDetailRow(rows, 'Sender bank code', senderBankCode, { copy: true })
      addTextDetailRow(rows, 'Sender BBAN', senderBban, { copy: true })
      addTextDetailRow(rows, 'Customer paid', payment.amountsBreakdown?.currency && payment.amountsBreakdown?.amount
        ? `${formatAssetAmount(payment.amountsBreakdown.amount, payment.amountsBreakdown.currency)} ${payment.amountsBreakdown.currency}`
        : undefined)
      addTextDetailRow(rows, 'Converted to', payment.amountsBreakdown?.onrampGrossAmount && payment.amountsBreakdown?.onrampNetwork
        ? (() => {
          const ticker = getMethod(payment.amountsBreakdown.onrampNetwork, payment.amountsBreakdown.onrampToken).ticker
          return `${exactAmount(payment.amountsBreakdown.onrampGrossAmount)} ${ticker}`
        })()
        : undefined)
      addTextDetailRow(rows, 'Fee', payment.amountsBreakdown?.onrampFee && payment.amountsBreakdown?.onrampNetwork
        ? `${exactAmount(payment.amountsBreakdown.onrampFee)} ${getMethod(payment.amountsBreakdown.onrampNetwork, payment.amountsBreakdown.onrampToken).ticker}`
        : undefined)
      addTextDetailRow(rows, 'Added to your balance', payment.amountsBreakdown?.onrampNetAmount && payment.amountsBreakdown?.onrampNetwork
        ? (() => {
          const ticker = getMethod(payment.amountsBreakdown.onrampNetwork, payment.amountsBreakdown.onrampToken).ticker
          return `${exactAmount(payment.amountsBreakdown.onrampNetAmount)} ${ticker}`
        })()
        : undefined)
      appendPaymentTransactionRows(rows, paymentData?.transactions)
      addTextDetailRow(rows, 'Payment event ID', payment.id, { copy: true, truncateLength: 8 })
      return rows
    }
    case TransactionKind.Payout: {
      const payout = transaction.payload as Payout
      const entryCount = payout.entryCount ||
        (payout.cryptoEntries?.length || 0) + (payout.fiatEntries?.length || 0) || payout.payouts?.length || 0
      const totalArrayEntries = (payout.cryptoEntries?.length ?? 0) + (payout.fiatEntries?.length ?? 0)
      const isSingleEntry = totalArrayEntries === 1

      if (!isSingleEntry) {
        addTextDetailRow(rows, 'Recipients', entryCount || undefined)
      }

      if (isSingleEntry) {
        const cryptoEntry = payout.cryptoEntries?.[0]
        const fiatEntry = payout.fiatEntries?.[0]

        if (cryptoEntry) {
          const method = getMethod(cryptoEntry.network, cryptoEntry.token)
          addTextDetailRow(rows, 'Recipient address', cryptoEntry.payoutAddress || cryptoEntry.externalAccountId, { copy: true, truncateLength: 10 })
          addTextDetailRow(rows, 'Asset', getMethodLabel(cryptoEntry.network, cryptoEntry.token))
          addTextDetailRow(rows, 'Amount', `${roundAssetAmount(method.ticker, Number(cryptoEntry.amount))} ${method.ticker}`)
          addTextDetailRow(rows, 'Status', cryptoEntry.status)
          addTextDetailRow(rows, 'Failure reason', cryptoEntry.failureReason)
        }

        if (fiatEntry) {
          const displayName = fiatEntry.beneficiaryName ?? fiatEntry.beneficiaryCompanyName
          const address = formatAddressParts(
            fiatEntry.beneficiaryAddressLine1,
            fiatEntry.beneficiaryAddressLine2,
            fiatEntry.beneficiaryCity,
            fiatEntry.beneficiaryState,
            fiatEntry.beneficiaryPostalCode,
            fiatEntry.beneficiaryCountry
          )
          addNodeDetailRow(rows, 'Recipient name', renderNameAndSecondary(displayName, fiatEntry.beneficiaryEmail))
          addTextDetailRow(rows, 'Address', address)
          addTextDetailRow(rows, 'IBAN', fiatEntry.iban, { copy: true })
          addTextDetailRow(rows, 'Sort code', fiatEntry.sortCode, { copy: true })
          addTextDetailRow(rows, 'Bank code', fiatEntry.bankCode, { copy: true })
          addTextDetailRow(rows, 'BBAN', fiatEntry.bban, { copy: true })
          addTextDetailRow(rows, 'External account', fiatEntry.externalAccountId, { copy: true, truncateLength: 8 })
          addTextDetailRow(rows, 'Amount', `${roundAssetAmount(fiatEntry.currency, Number(fiatEntry.amount))} ${fiatEntry.currency}`)
          addTextDetailRow(rows, 'Status', fiatEntry.status)
          addTextDetailRow(rows, 'Failure reason', fiatEntry.failureReason)
        }
      }

      addTextDetailRow(rows, 'Confirmed', payout.confirmedAt ? formatDrawerDate(payout.confirmedAt) : undefined)
      addTextDetailRow(rows, 'Completed', payout.completedAt ? formatDrawerDate(payout.completedAt) : undefined)
      addTextDetailRow(rows, 'Payout ID', payout.id, { copy: true, truncateLength: 8 })
      return rows
    }
    case TransactionKind.Refund: {
      const refund = transaction.payload as RefundEntityWithRelatedCustomer
      const method = getMethod(refund.network, refund.token)

      addNodeDetailRow(rows, 'Recipient', renderNameAndSecondary(
        refund.relatedCustomer?.name,
        refund.relatedCustomer?.email
      ))
      appendPayerFinancialAddressRows(rows, refund.recipientAccount, 'Recipient')
      addTextDetailRow(rows, 'Recipient address', refund.recipientAddress, { copy: true, truncateLength: 10 })
      appendWithdrawalDestinationRows(
        rows,
        supplements.withdrawalEventData,
        supplements.withdrawalEventData?.receipt,
        supplements.withdrawalAccount
      )
      addTextDetailRow(rows, 'Asset', `${method.ticker} on ${method.hostChain?.name ?? method.name}`)
      addTextDetailRow(rows, 'Associated entity', refund.associatedEntityId, { copy: true, truncateLength: 8 })
      addTextDetailRow(rows, 'Withdrawal ID', refund.withdrawalId, { copy: true, truncateLength: 8 })
      addTextDetailRow(rows, 'Provider refund ID', refund.ivyOrderId, { copy: true, truncateLength: 8 })
      addTextDetailRow(rows, 'Failure reason', refund.failureReason ?? supplements.withdrawalEventData?.failureReason)
      addTextDetailRow(rows, 'Refund ID', refund.id, { copy: true, truncateLength: 8 })
      return rows
    }
    case TransactionKind.Swap: {
      const swap = transaction.payload as Swap
      const fromMethod = getMethod(swap.fromNetwork, swap.fromToken)
      const toMethod = getMethod(swap.toNetwork, swap.toToken)

      addTextDetailRow(rows, 'From', `${roundAssetAmount(fromMethod.ticker, Number(swap.fromAmount))} ${fromMethod.ticker} on ${swap.fromNetwork}`)
      addTextDetailRow(rows, 'To', swap.toAmount ? `${roundAssetAmount(toMethod.ticker, Number(swap.toAmount))} ${toMethod.ticker} on ${swap.toNetwork}` : `${toMethod.ticker} on ${swap.toNetwork}`)
      if (swap.toAmount) {
        const rate = Number(swap.toAmount) / Number(swap.fromAmount)
        addTextDetailRow(rows, 'Execution rate', `1 ${fromMethod.ticker} = ${roundAssetAmount(toMethod.ticker, rate)} ${toMethod.ticker}`)
      }
      addTextDetailRow(rows, 'Updated', formatDrawerDate(swap.updatedAt))
      addTextDetailRow(rows, 'Failure reason', swap.failReason)
      addTextDetailRow(rows, 'Swap ID', swap.id, { copy: true, truncateLength: 8 })
      return rows
    }
    case TransactionKind.Credit:
    case TransactionKind.Debit: {
      const adjustment = transaction.payload as BalanceAdjustmentTransaction
      const method = getMethod(adjustment.network, adjustment.token)
      addTextDetailRow(rows, 'Asset', `${method.ticker} on ${method.hostChain?.name ?? method.name}`)
      addTextDetailRow(rows, 'Booked', formatDrawerDate(adjustment.appliedAt))
      addTextDetailRow(rows, 'Made by', 'Radom')
      addTextDetailRow(rows, 'Reference', adjustment.id, { copy: true, truncateLength: 8 })
      return rows
    }
    default: {
      const deposit = transaction.payload as Deposit
      const eventData = supplements.depositEventData
      const isFiatDeposit = !!deposit.depositMethod.Fiat
      const method = deposit.depositMethod.Crypto
        ? getMethod(deposit.depositMethod.Crypto.network, deposit.depositMethod.Crypto.token)
        : getMethod(deposit.depositMethod.Fiat?.onrampNetwork || '', deposit.depositMethod.Fiat?.onrampToken)
      const senderAddresses = eventData?.senderAddresses ?? deposit.depositTransaction?.senderAddresses ?? []
      const beneficiaryDetails = getDepositBeneficiaryDetails(deposit, eventData)

      addTextDetailRow(rows, 'Deposit account', deposit.depositAccountName || deposit.depositAccountId)

      if (isFiatDeposit) {
        addTextDetailRow(
          rows,
          'Bank deposit type',
          inferDepositRailLabel(beneficiaryDetails) ??
            `${deposit.depositMethod.Fiat?.fiatCurrency ?? 'Fiat'} bank deposit`
        )
        appendDepositBeneficiaryRows(rows, beneficiaryDetails)
        if (!beneficiaryDetails) {
          addTextDetailRow(rows, 'Sender bank details', 'Not returned for this transaction')
        }
      } else {
        addTextDetailRow(rows, 'Asset', `${method.ticker} on ${method.hostChain?.name ?? method.name}`)
        senderAddresses.forEach((sender, idx) => addTextDetailRow(rows, idx === 0 ? 'Sender address' : `Sender address ${idx + 1}`, sender.address, {
          copy: true,
          truncateLength: 10
        }))
      }

      addTextDetailRow(rows, 'Deposit ID', deposit.id, { copy: true, truncateLength: 8 })
      return rows
    }
    }
  }

  const getDrawerHeroTitle = (
    transaction: ExtendedTransaction,
    supplements: TransactionDrawerSupplements = {}
  ): string => {
    switch (transaction.kind) {
    case TransactionKind.Deposit:
      return isFiatDepositTransaction(transaction) ? 'Fiat deposit' : 'Crypto deposit'
    case TransactionKind.Withdrawal: {
      const isFiatWithdrawal =
        supplements.withdrawalEventData?.destination ||
        (transaction.payload as WithdrawalRequest).receipt?.FiatReceipt
      return isFiatWithdrawal
        ? 'Fiat withdrawal'
        : 'Crypto withdrawal'
    }
    case TransactionKind.Payment:
      return getPaymentDisplayLabel(transaction.payload as ManagedEventWithRelatedCustomer)
    case TransactionKind.Payout: {
      const payout = transaction.payload as Payout
      const hasFiat = (payout.fiatEntries?.length ?? 0) > 0
      const hasCrypto = (payout.cryptoEntries?.length ?? 0) > 0
      return hasFiat && hasCrypto ? 'Mixed payout' : hasFiat ? 'Fiat payout' : 'Crypto payout'
    }
    case TransactionKind.Refund:
      return 'Refund'
    case TransactionKind.Swap:
      return 'Conversion'
    case TransactionKind.Credit:
    case TransactionKind.Debit:
      return ADJUSTMENT_TITLES[transaction.kind]
    default:
      return transaction.kind
    }
  }

  const getHeroBalanceMetric = (
    unified: ManagedBalanceLedger[]
  ): { value: string, subline?: string } | undefined => {
    const balanceEntry = unified[0]
    if (!balanceEntry) return undefined
    const ticker = ledgerTicker(balanceEntry)
    return {
      value: `${roundAssetAmount(ticker, Number(balanceEntry.balanceBefore))} → ${roundAssetAmount(ticker, Number(balanceEntry.balanceAfter))} ${ticker}`,
      subline: ledgerScope(balanceEntry)
    }
  }

  const getDetailsSectionTitle = (transaction: ExtendedTransaction): string => {
    switch (transaction.kind) {
    case TransactionKind.Deposit:
      return isFiatDepositTransaction(transaction) ? 'Bank details' : 'Deposit details'
    case TransactionKind.Payment:
      return 'Payment details'
    case TransactionKind.Withdrawal:
      return 'Destination details'
    case TransactionKind.Payout: {
      const payoutPayload = transaction.payload as Payout
      const payoutArrayEntries = (payoutPayload.cryptoEntries?.length ?? 0) + (payoutPayload.fiatEntries?.length ?? 0)
      return payoutArrayEntries === 1 ? 'Recipient details' : 'Payout details'
    }
    case TransactionKind.Refund:
      return 'Refund destination'
    case TransactionKind.Swap:
      return 'Conversion details'
    case TransactionKind.Credit:
    case TransactionKind.Debit:
      return 'Adjustment details'
    default:
      return 'Details'
    }
  }

  const renderPayoutRecipientsSection = (): ReactElement | null => {
    if (!selectedTransaction || selectedTransaction.kind !== TransactionKind.Payout) return null
    const payout = selectedTransaction.payload as Payout
    const totalArrayEntries = (payout.cryptoEntries?.length ?? 0) + (payout.fiatEntries?.length ?? 0)
    if (totalArrayEntries <= 1) return null

    return <DrawerCard>
      <DrawerSectionTitle>Recipients</DrawerSectionTitle>
      <PayoutRecipients
        cryptoEntries={payout.cryptoEntries}
        fiatEntries={payout.fiatEntries}
        viewAllHref={selectedTransaction.url} />
    </DrawerCard>
  }

  const completeSelectedPayment = useCallback(async (): Promise<void> => {
    if (!selectedTransaction) return
    setIsCompletingPayment(true)
    try {
      await Radom.completePayment({ eventId: selectedTransaction.id })
      setIsIncompleteModalOpen(false)
      selectTransaction(undefined)
      await fetchTransactions({
        page: curPage,
        kinds: transactionKinds,
        statuses: transactionStatuses,
        isTestMode: testMode,
        search: transactionSearch
      })
    } catch (err: any) {
      errorToast(err.reason || err.message || err.toString())
    } finally {
      setIsCompletingPayment(false)
    }
  }, [selectedTransaction, curPage, transactionKinds, transactionStatuses, testMode, transactionSearch])

  const renderTransactionDrawer = (): ReactElement | null => {
    if (!selectedTransaction) return null

    const ledger = getTransactionLedger(selectedTransaction)
    const unified = unifyLedger(ledger)
    const drawerSupplements = {
      depositEventData: selectedDepositEventData,
      withdrawalEventData: selectedWithdrawalEventData,
      withdrawalAccount: selectedWithdrawalAccount
    }
    const hashLinks = getTransactionHashLinks(selectedTransaction, drawerSupplements)
    const specificRows = getTransactionSpecificRows(selectedTransaction, drawerSupplements)
    const balanceMetric = getHeroBalanceMetric(unified)
    const hasFee = normalizeDetailText(selectedTransaction.summary.fee.ticker)
    const drawerTitle = getDrawerHeroTitle(selectedTransaction, drawerSupplements)
    const shouldHideNetworkReferences =
      isFiatDepositTransaction(selectedTransaction) ||
      isFiatWithdrawalTransaction(selectedTransaction, drawerSupplements) ||
      selectedTransaction.kind === TransactionKind.Swap ||
      isAdjustmentKind(selectedTransaction.kind)
    const referenceHashLinks = shouldHideNetworkReferences ? [] : hashLinks

    return <>
      <DetailsDrawer
        visible={!!selectedTransaction}
        onClose={() => selectTransaction(undefined)}
        eyebrow='Transaction details'
        title={drawerTitle}
        subtitle={renderCopyReference(selectedTransaction.id, 6)}
        width={560}
        ariaLabel={`${drawerTitle} transaction details`}
      >
        <DrawerHeroCard>
          <DrawerHeroTop>
            <DrawerHeroIdentity>
              <DrawerHeroIcon>
                <img src={TRANSACTION_ICONS[selectedTransaction.kind] as string} />
              </DrawerHeroIcon>
              <DrawerHeroText>
                <DrawerHeroTitle>{drawerTitle}</DrawerHeroTitle>
                <DrawerHeroMeta>{formatDrawerDate(selectedTransaction.createdAt)}</DrawerHeroMeta>
              </DrawerHeroText>
            </DrawerHeroIdentity>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <TransactionStatusBadge status={selectedTransaction.status}>
                {transactionStatusLabel(selectedTransaction.status)}
              </TransactionStatusBadge>
              {selectedTransaction.kind === TransactionKind.Payout &&
                (selectedTransaction.payload as Payout).processingStage && (
                <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 12 }}>
                  {(selectedTransaction.payload as Payout).processingStage}
                  {STAGE_EXPECTATIONS[(selectedTransaction.payload as Payout).processingStage ?? ''] &&
                    ` · ${STAGE_EXPECTATIONS[(selectedTransaction.payload as Payout).processingStage ?? '']}`}
                </span>
              )}
            </div>
          </DrawerHeroTop>
          <DrawerHero>
            <DrawerHeroAmount>
              <DrawerHeroAmountValue style={{ color: selectedTransaction.summary.in.color }}>
                {selectedTransaction.summary.in.amount}
              </DrawerHeroAmountValue>
              <DrawerHeroTicker>{selectedTransaction.summary.in.ticker}</DrawerHeroTicker>
            </DrawerHeroAmount>
            {selectedTransaction.summary.out
              ? <ConversionLine>
                <Chevron style={{ opacity: 0.6, transform: 'rotate(-90deg)', width: 12, height: 12 }} />
                <span style={{ color: selectedTransaction.summary.out.color }}>
                  {selectedTransaction.summary.out.amount}
                </span>
                <AmountTicker style={{ color: selectedTransaction.summary.out.color }}>
                  {selectedTransaction.summary.out.ticker}
                </AmountTicker>
              </ConversionLine>
              : renderPayoutLedgerAmount(selectedTransaction, unified)}
          </DrawerHero>
          {(hasFee || balanceMetric) && <DrawerMetricGrid>
            {hasFee && <DrawerMetric>
              <DrawerMetricLabel>Fee</DrawerMetricLabel>
              <DrawerMetricValue>{selectedTransaction.summary.fee.amount || '0'} {selectedTransaction.summary.fee.ticker}</DrawerMetricValue>
            </DrawerMetric>}
            {balanceMetric && <DrawerMetric>
              <DrawerMetricLabel>Balance before → after</DrawerMetricLabel>
              <DrawerMetricValue>{balanceMetric.value}</DrawerMetricValue>
              {balanceMetric.subline && <DrawerSubline>{balanceMetric.subline}</DrawerSubline>}
            </DrawerMetric>}
          </DrawerMetricGrid>}
        </DrawerHeroCard>

        {(specificRows.length > 0 || isDrawerSupplementLoading) && <DrawerCard>
          <DrawerSectionTitle>{getDetailsSectionTitle(selectedTransaction)}</DrawerSectionTitle>
          {isDrawerSupplementLoading && <DrawerEmptyNote>Loading settlement details...</DrawerEmptyNote>}
          {specificRows.map((row, idx) => <DrawerDetailRow key={`${row.label}-${idx}`}>
            <DrawerDetailLabel>{row.label}</DrawerDetailLabel>
            <DrawerDetailValue>{row.value}</DrawerDetailValue>
          </DrawerDetailRow>)}
        </DrawerCard>}

        {renderPayoutRecipientsSection()}

        {referenceHashLinks.length > 0 && <DrawerCard>
          <DrawerSectionTitle>Network references</DrawerSectionTitle>
          {referenceHashLinks.map((item, idx) => {
            const explorerUrl = item.network && EXPLORER_URLS[item.network]
              ? formatExplorerUrl(item.network, 'tx', item.hash)
              : undefined
            return <DrawerDetailRow key={`${item.hash}-${idx}`}>
              <DrawerDetailLabel>{idx === 0 ? 'Transaction hash' : `Hash ${idx + 1}`}</DrawerDetailLabel>
              <DrawerDetailValue>
                <DrawerReferenceInline>
                  <DrawerMono>{shortAddress(item.hash, 10)}</DrawerMono>
                  <CopyButton text={item.hash} />
                  {explorerUrl && <a
                    href={explorerUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={e => e.stopPropagation()}
                    style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--text-faint, #7b8494)' }}
                  >
                    <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor'
                      strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                      <path d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6'/>
                      <polyline points='15 3 21 3 21 9'/>
                      <line x1='10' y1='14' x2='21' y2='3'/>
                    </svg>
                  </a>}
                </DrawerReferenceInline>
              </DrawerDetailValue>
            </DrawerDetailRow>
          })}
        </DrawerCard>}
        {isIncompletePayment && has('WriteIncompletePayments') &&
            <PrimaryButton
              style={{ width: '100%' }}
              onClick={() => setIsIncompleteModalOpen(true)}
            >
              Complete payment
            </PrimaryButton>}
        {!!bankPaymentCheckoutSessionId &&
            <SecondaryButton
              type='button'
              style={{ width: '100%' }}
              onClick={() => {
                const checkoutSessionId = bankPaymentCheckoutSessionId
                selectTransaction(undefined)
                OpenBankingPaymentController.open(checkoutSessionId)
              }}
            >
              View bank payment history
            </SecondaryButton>}
        {!!refundableCheckoutSessionId && has('WriteRefunds') &&
            <PrimaryButton
              style={{ width: '100%' }}
              isLoading={isLoadingRefundSession}
              onClick={() => { void openRefundForm() }}
            >
              Refund payment
            </PrimaryButton>}
      </DetailsDrawer>

      {
        isIncompleteModalOpen &&
        <Modal
          title='Complete payment'
          visible={isIncompleteModalOpen}
          onClose={() => setIsIncompleteModalOpen(false)}
          innerStyles={{ maxWidth: '200px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <span style={{ fontSize: 14, color: RADOM_COLORS.GRAY_DARKEST, whiteSpace: 'pre-wrap' }}>
                Are you sure you would like to mark this payment as complete?
                This will trigger any post-payment actions and is irreversible.
            </span>
            <PrimaryButton
              isLoading={isCompletingPayment}
              style={{ width: '100%' }}
              onClick={completeSelectedPayment}>
                Complete payment
            </PrimaryButton>
          </div>
        </Modal>
      }
    </>
  }

  const renderKindFilterMenu = (): ReactElement => (
    <FilterMenu>
      <FilterMenuSection>
        <FilterMenuTitle>Presets</FilterMenuTitle>
        <FilterPresetGrid>
          {TRANSACTION_KIND_PRESETS.map(preset => (
            <FilterMenuButton
              key={preset.label}
              type='button'
              $active={sameMembers(transactionKinds, preset.kinds)}
              onClick={() => setTransactionKinds(preset.kinds)}
            >
              {preset.label}
            </FilterMenuButton>
          ))}
        </FilterPresetGrid>
      </FilterMenuSection>
      <FilterMenuSection>
        <FilterMenuTitle>Types</FilterMenuTitle>
        <FilterOptionGrid>
          {allKinds.map(kind => (
            <FilterOptionRow
              key={kind}
              type='button'
              $active={transactionKinds.includes(kind)}
              onClick={() => toggleTransactionKind(kind)}
            >
              <FilterCheck $active={transactionKinds.includes(kind)} />
              <span>{kind}</span>
            </FilterOptionRow>
          ))}
        </FilterOptionGrid>
      </FilterMenuSection>
    </FilterMenu>
  )

  const renderStatusFilterMenu = (): ReactElement => (
    <FilterMenu>
      <FilterMenuSection>
        <FilterMenuTitle>Presets</FilterMenuTitle>
        <FilterPresetGrid>
          {TRANSACTION_STATUS_PRESETS.map(preset => (
            <FilterMenuButton
              key={preset.label}
              type='button'
              $active={sameMembers(transactionStatuses, preset.statuses)}
              onClick={() => setTransactionStatuses(preset.statuses)}
            >
              {preset.label}
            </FilterMenuButton>
          ))}
        </FilterPresetGrid>
      </FilterMenuSection>
      <FilterMenuSection>
        <FilterMenuTitle>Statuses</FilterMenuTitle>
        <FilterOptionGrid>
          {allStatuses.map(status => (
            <FilterOptionRow
              key={status}
              type='button'
              $active={transactionStatuses.includes(status)}
              onClick={() => toggleTransactionStatus(status)}
            >
              <FilterCheck $active={transactionStatuses.includes(status)} />
              <span>{transactionStatusLabel(status)}</span>
            </FilterOptionRow>
          ))}
        </FilterOptionGrid>
      </FilterMenuSection>
    </FilterMenu>
  )

  useEffect(() => {
    debouncedFetch({
      page: curPage,
      kinds: transactionKinds,
      statuses: transactionStatuses,
      isTestMode: testMode,
      search: transactionSearch
    })
    return () => debouncedFetch.cancel()
  }, [curPage, transactionKinds, transactionStatuses, testMode, transactionSearch, debouncedFetch])

  useEffect(() => {
    setCurPage(0)
  }, [testMode, transactionKinds, transactionStatuses, transactionSearch])

  useEffect(() => {
    const complete = transactions.filter(item => !!item.payload)
    if (complete.length !== transactions.length) {
      console.warn('Transactions without a payload were skipped', transactions.filter(item => !item.payload).map(item => item.id))
    }
    setExtendedTransactions(
      complete.map((item) =>
        ({
          ...item,
          status: getTransactionStatus(item),
          summary: getTransactionSummary(item),
          url: getUrl(item),
          related: getTransactionRelated(item)
        }))
    )
  }, [transactions])

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth <= 900)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const renderDetectedRow = (row: DetectedRow): ReactElement[] => {
    const method = getMethod(row.network, row.token)
    const kind = row.kind === 'deposit' ? TransactionKind.Deposit : TransactionKind.Payment
    return [
      <TransactionCell key={'1'}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <img src={TRANSACTION_ICONS[kind] as string} style={{ width: 18, height: 18 }} />
          <span style={{ color: RADOM_COLORS.BLACK, fontWeight: 500 }}>{kind}</span>
        </div>
        <TransactionMetaLine>
          <span>TX</span>
          <span style={{ fontFamily: 'var(--font-mono)', color: '#4b5563' }}>
            {shortAddress(row.transactionHash, 6)}
          </span>
          <span onClick={e => e.stopPropagation()}>
            <CopyButton text={row.transactionHash} />
          </span>
        </TransactionMetaLine>
      </TransactionCell>,
      <div key={'2'} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <TransactionStatusBadge status={TransactionStatus.InReview}>{detectedStatusLabel}</TransactionStatusBadge>
        <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 11.5 }}>{detectedProgressLabel(row)}</span>
      </div>,
      <RelatedCell key={'3'} onClick={(e) => e.stopPropagation()}>
        {row.related.url.length > 0
          ? <Link to={row.related.url} style={{ color: RADOM_COLORS.BLACK }}>{row.related.name}</Link>
          : <span style={{ color: RADOM_COLORS.BLACK }}>{row.related.name}</span>}
        <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 12.5 }}>{row.related.subtitle}</span>
      </RelatedCell>,
      <AmountCell key={'4'}>
        <AmountLine>
          <span style={{ color: MONEY_IN_COLOR }}>+ {roundAssetAmount(method.ticker, row.amount)}</span>
          <AmountTicker style={{ color: MONEY_IN_COLOR }}>{method.ticker}</AmountTicker>
        </AmountLine>
        <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 11.5 }}>not yet credited</span>
      </AmountCell>,
      ...(!isMobile
        ? [<div key={'5'}><FeeLine><span style={{ color: RADOM_COLORS.GRAY4 }}>-</span></FeeLine></div>,
          <div key={'6'}><span style={{ color: RADOM_COLORS.GRAY4 }}>-</span></div>,
          <div key={'7'}>
            <TimeText textColor={RADOM_COLORS.GRAY_DARKER} date={new Date(row.seenAt)}
              formatModes={[TimeFormatType.ShortUTCJustDay]} />
          </div>]
        : [])
    ]
  }

  return <div style={{ display: 'flex', width: '100%', padding: '30px 48px', flexDirection: 'column', gap: '20px' }}>
    <ListPage>
      <PageHeaderRow>
        <PageHeader title='Transactions' subTitle='Manage your transactions'/>
        <PageActionRow>
          <Dropdown
            noChevron
            selectedContent={<Dots width={15} />}
            dropdownContent={
              <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                <DropdownItem onClick={() => setExportTransactionsModalOpen(true)}>Export transactions</DropdownItem>
              </div>
            }
          />
        </PageActionRow>
      </PageHeaderRow>
      <FiltersPanel>
        <FiltersRow>
          <FilterSearchWrap>
            <TextInput
              value={transactionSearch}
              onChange={e => setTransactionSearch(e.target.value)}
              placeholder='Search ID, hash, contact, or address'
            />
          </FilterSearchWrap>
          <FilterControls>
            <Dropdown
              preferredAlignment='right'
              matchTriggerWidth={false}
              outerStyle={{ minWidth: 164, minHeight: 42, padding: '7px 11px' }}
              innerStyle={{ borderRadius: 12, maxHeight: '70vh' }}
              selectedContent={
                <FilterTrigger>
                  <FilterTriggerLabel>Type</FilterTriggerLabel>
                  <FilterTriggerValue>{transactionKindFilterLabel}</FilterTriggerValue>
                </FilterTrigger>
              }
              dropdownContent={renderKindFilterMenu()}
            />
            <Dropdown
              preferredAlignment='right'
              matchTriggerWidth={false}
              outerStyle={{ minWidth: 164, minHeight: 42, padding: '7px 11px' }}
              innerStyle={{ borderRadius: 12, maxHeight: '70vh' }}
              selectedContent={
                <FilterTrigger>
                  <FilterTriggerLabel>Status</FilterTriggerLabel>
                  <FilterTriggerValue>{transactionStatusFilterLabel}</FilterTriggerValue>
                </FilterTrigger>
              }
              dropdownContent={renderStatusFilterMenu()}
            />
            <FilterCount>{visibleDetected.length + filteredTransactions.length} shown</FilterCount>
            <FilterResetButton type='button' onClick={resetFilters} disabled={!hasActiveFilters}>Reset</FilterResetButton>
          </FilterControls>
        </FiltersRow>
      </FiltersPanel>

      <ListTableSurface>
        <Table
          borderless
          headers={['Transaction', 'Status', 'Related', 'Amount', ...(!isMobile ? ['Fee', 'Balance', 'Date'] : [])].map(h => <span key={h}>{h}</span>)}
          onClickRow={i => {
            if (i < visibleDetected.length) {
              setSelectedDetectedId(visibleDetected[i].id)
              return
            }
            const s = filteredTransactions[i - visibleDetected.length]
            selectTransaction(s?.id)
          }}
          isRowClickable={i => i < visibleDetected.length || !!filteredTransactions[i - visibleDetected.length]}
          isLoading={isLoading}
          items={[...visibleDetected.map(renderDetectedRow), ...filteredTransactions.map(s => {
            const ledger = getTransactionLedger(s)
            const unified = unifyLedger(ledger)
            return [
              <TransactionCell key={'1'}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <img src={TRANSACTION_ICONS[s.kind] as string} style={{ width: 18, height: 18 }} />
                  <span style={{ color: RADOM_COLORS.BLACK, fontWeight: 500 }}>
                    {s.kind === TransactionKind.Payment
                      ? getPaymentDisplayLabel(s.payload as ManagedEventWithRelatedCustomer)
                      : s.kind === TransactionKind.Swap
                        ? 'Conversion'
                        : isAdjustmentKind(s.kind) ? ADJUSTMENT_TITLES[s.kind] : s.kind}
                  </span>
                </div>
                <TransactionMetaLine>
                  <span>ID</span>
                  <span style={{ fontFamily: 'var(--font-mono)', color: '#4b5563' }}>
                    {shortAddress(s.id, 6)}
                  </span>
                  <span onClick={e => e.stopPropagation()}>
                    <CopyButton text={s.id} />
                  </span>
                </TransactionMetaLine>
              </TransactionCell>,
              <div key={'2'} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <TransactionStatusBadge status={s.status}>{transactionStatusLabel(s.status)}</TransactionStatusBadge>
                {s.kind === TransactionKind.Payout &&
                  (s.payload as Payout).processingStage && (
                  <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 11.5 }}>
                    {(s.payload as Payout).processingStage}
                    {STAGE_EXPECTATIONS[(s.payload as Payout).processingStage ?? ''] &&
                      ` · ${STAGE_EXPECTATIONS[(s.payload as Payout).processingStage ?? '']}`}
                  </span>
                )}
                {s.kind === TransactionKind.Swap &&
                  s.status === TransactionStatus.Pending && (
                  <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 11.5 }}>
                    settling · usually ~15 minutes
                  </span>
                )}
              </div>,
              <RelatedCell key={'3'} onClick={(e) => e.stopPropagation()}>
                {s.related.url.length > 0
                  ? <Link to={s.related.url} style={{ color: RADOM_COLORS.BLACK }}>
                    {s.related.name}
                  </Link>
                  : <span style={{ color: RADOM_COLORS.BLACK }}>{s.related.name}</span>}
                <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 12.5 }}>
                  {s.kind === TransactionKind.Deposit
                    ? 'Deposit address'
                    : s.kind === TransactionKind.Payment
                      ? 'Contact'
                      : s.kind === TransactionKind.Refund
                        ? 'Contact'
                        : s.kind === TransactionKind.Withdrawal
                          ? 'Destination'
                          : s.kind === TransactionKind.Swap
                            ? 'Conversion'
                            : isAdjustmentKind(s.kind)
                              ? 'Adjustment'
                              : ((s.payload as Payout).entryCount ?? 1) > 1
                                ? 'Payout batch'
                                : 'Recipient'}
                </span>
              </RelatedCell>,
              <AmountCell key={'4'}>
                <AmountLine>
                  <span style={{ color: s.summary.in.color }}>
                    {s.summary.in.amount}
                  </span>
                  <AmountTicker style={{ color: s.summary.in.color }}>
                    {s.summary.in.ticker}
                  </AmountTicker>
                </AmountLine>
                {
                  s.summary.out
                    ? (
                      <ConversionLine>
                        <Chevron style={{ opacity: 0.6, transform: 'rotate(-90deg)', width: 12, height: 12 }} />
                        <span style={{ color: s.summary.out.color }}>
                          {s.summary.out.amount}
                        </span>
                        <AmountTicker style={{ color: s.summary.out.color }}>
                          {s.summary.out.ticker}
                        </AmountTicker>
                      </ConversionLine>
                    )
                    : renderPayoutLedgerAmount(s, unified)
                }
              </AmountCell>,
              ...(!isMobile
                ? [<div key={'5'}>
                  <FeeLine>
                    <span>{s.summary.fee.amount}</span>
                    <span style={{ color: RADOM_COLORS.GRAY4, fontWeight: 400 }}>{s.summary.fee.ticker}</span>
                  </FeeLine>
                </div>,
                <div key={'6'} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {unified.map((balanceEntry, idx) => {
                    const ticker = ledgerTicker(balanceEntry)

                    return <BalanceEntry key={idx}>
                      <div>
                        <span style={{ color: RADOM_COLORS.GRAY4 }}>
                          {roundAssetAmount(ticker, Number(balanceEntry.balanceBefore))}
                        </span>
                        <span style={{ color: RADOM_COLORS.GRAY4, margin: '0 5px' }}>→</span>
                        <span style={{ fontWeight: 500 }}>
                          {roundAssetAmount(ticker, Number(balanceEntry.balanceAfter))}
                        </span>
                        <span style={{ color: RADOM_COLORS.GRAY4, marginLeft: 6 }}>
                          {ticker}
                        </span>
                      </div>
                      <BalanceMeta>{ledgerScope(balanceEntry)}</BalanceMeta>
                    </BalanceEntry>
                  }
                  )}
                </div>,
                <div key={'7'}>
                  <TimeText textColor={RADOM_COLORS.GRAY_DARKER} date={new Date(s.createdAt)}
                    formatModes={[TimeFormatType.ShortUTCJustDay]} />
                </div>]
                : [])
            ]
          })]}
          emptyPlaceholderText={transactionSearch.trim().length > 0 ? 'No matching transactions in the current results' : 'No transactions yet'}
        />
      </ListTableSurface>
      {
        !isLoading && <ListPaginationRow>
          <Pagination
            curPage={curPage}
            numPages={Math.ceil(totalTransactions / pageSize)}
            onPageClick={page => setCurPage(page)}
          />
        </ListPaginationRow>
      }
      {
        exportTransactionsModalOpen &&
      <Modal visible={exportTransactionsModalOpen} onClose={() => setExportTransactionsModalOpen(false)} title='Export transactions'>
        <ExportTransactionsForm />
      </Modal>
      }
      {
        !!refundSession &&
      <Modal
        title='Refund payment'
        visible={!!refundSession}
        onClose={() => setRefundSession(undefined)}>
        <div style={{ width: 500, maxWidth: '90vw', padding: '0 30px 30px 30px' }}>
          <RefundForm
            checkoutSession={refundSession}
            onRefund={() => {
              setRefundSession(undefined)
              void fetchTransactions({
                page: curPage,
                kinds: transactionKinds,
                statuses: transactionStatuses,
                isTestMode: testMode,
                search: transactionSearch
              })
            }} />
        </div>
      </Modal>
      }
      {renderTransactionDrawer()}
      {selectedDetected && <DetectedTransactionDrawer
        row={selectedDetected}
        onClose={() => setSelectedDetectedId(undefined)}
      />}
    </ListPage>
  </div>
})

export default Transactions
